import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { formatBusinessDescription } from '@/lib/utils';
import { Users, Package, Briefcase, ExternalLink, Rocket, ArrowRight, Pencil, Plus, User, ChevronsUpDown, Lock, Unlock, Trash2, X, Globe, UserCircle, Shield, Palette, Gem, Download, AlertCircle } from 'lucide-react';
// import googleAds from '/google-ads.svg'; // Temporarily unused - budget tab disabled
import { Card, CardContent } from '@/components/ui/card';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { StrategyContextPanel } from '@/components/business/strategy-doc-panel';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { YourGoalKpiCard, type KpiTargetsSaveControl } from '@/components/strategy/your-goal-kpi-card';
import { useAtomValue, useSetAtom } from 'jotai';
import { organizationDataAtom, googleAdsAccountAtom, campaignsDataAtom, manualBrandEntryAtom, selectedBusinessTabAtom } from '@/store';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Business, CampaignData, BrandIdentity, Icp, ProductData, BusinessSettings, PlatformBudget, ProductIcpLink } from '@/lib/types';
import { getCampaignTypeLabel } from '@/lib/types';
import { EmptyState, PageEmptyState, PageErrorState } from '@/components/glitch-ui/empty-state';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { EditableField } from '@/components/glitch-ui/editable-field';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { campaignsQueryOptions, useSaveBusinessMutation, businessTaskTrackingQueryOptions, businessQueryOptions, useGenerateBrandIntelMutation, useGenerateProductMutation, useGeneratePersonaMutation } from '@/lib/query-options';
import { BusinessLocations, type LocationsSaveControl } from '@/components/business/business-locations';
import { BusinessTrustSignalView } from '@/components/business/business-trust-signal-view';
import { BudgetV2 } from '@/components/business/budget-v2';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';
import { GenerateItemModal } from '@/components/business/generate-item-modal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/glitch-ui/slider';
import { Progress } from '@/components/ui/progress';
import { useBusinessPDFExport } from '@/hooks/useBusinessPDFExport';
import { useTestMode } from '@/hooks/useTestMode';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { useToast } from '@/hooks/useToast';

// Trust signal type - matches the component interface
interface TrustSignal {
  id: string;
  name: string;
}

export const Route = createFileRoute('/_authenticated/_layout/business/')({
  component: withOrgReset(BusinessPage),
  staticData: {
    title: 'Business',
    icon: Rocket,
  },
});

// Industry/Vertical options - comprehensive list with compliance tiers (alphabetical)
const INDUSTRIES = [
  { value: 'accounting', label: 'Accounting & Tax Services', tier: 3 },
  { value: 'agriculture', label: 'Agriculture & Farming', tier: 3 },
  { value: 'alcohol', label: 'Alcohol & Spirits', tier: 1 },
  { value: 'architecture', label: 'Architecture & Design', tier: 3 },
  { value: 'arts-culture', label: 'Arts & Culture', tier: 3 },
  { value: 'automotive', label: 'Automotive', tier: 3 },
  { value: 'aviation', label: 'Aviation Services', tier: 3 },
  { value: 'beauty', label: 'Beauty & Cosmetics', tier: 2 },
  { value: 'cannabis', label: 'Cannabis', tier: 1 },
  { value: 'childcare', label: 'Childcare & Family Services', tier: 2 },
  { value: 'cleaning', label: 'Cleaning & Janitorial', tier: 3 },
  { value: 'construction', label: 'Construction & Trades', tier: 3 },
  { value: 'consulting', label: 'Consulting', tier: 3 },
  { value: 'dating', label: 'Dating Services', tier: 2 },
  { value: 'ecommerce', label: 'E-commerce & Retail', tier: 3 },
  { value: 'education', label: 'Education', tier: 2 },
  { value: 'energy-utilities', label: 'Energy & Utilities', tier: 3 },
  { value: 'engineering', label: 'Engineering Services', tier: 3 },
  { value: 'entertainment', label: 'Entertainment & Media', tier: 3 },
  { value: 'environmental', label: 'Environmental Services', tier: 3 },
  { value: 'events-wedding', label: 'Events & Wedding Services', tier: 3 },
  { value: 'financial-services', label: 'Financial Services', tier: 1 },
  { value: 'food-beverage', label: 'Food & Beverage', tier: 3 },
  { value: 'funeral', label: 'Funeral Services', tier: 3 },
  { value: 'gambling', label: 'Gambling & Gaming', tier: 1 },
  { value: 'government', label: 'Government & Public Sector', tier: 3 },
  { value: 'health-wellness', label: 'Health & Wellness', tier: 2 },
  { value: 'healthcare', label: 'Healthcare', tier: 1 },
  { value: 'insurance', label: 'Insurance', tier: 1 },
  { value: 'landscaping', label: 'Landscaping & Gardening', tier: 3 },
  { value: 'legal', label: 'Legal Services', tier: 1 },
  { value: 'logistics', label: 'Logistics & Shipping', tier: 3 },
  { value: 'manufacturing', label: 'Manufacturing', tier: 3 },
  { value: 'marine', label: 'Marine & Boating', tier: 3 },
  { value: 'marketing-agency', label: 'Marketing & Advertising', tier: 3 },
  { value: 'media-publishing', label: 'Media & Publishing', tier: 3 },
  { value: 'mining', label: 'Mining & Natural Resources', tier: 3 },
  { value: 'music-audio', label: 'Music & Audio Production', tier: 3 },
  { value: 'nonprofit', label: 'Non-profit & Charity', tier: 3 },
  { value: 'pest-control', label: 'Pest Control', tier: 3 },
  { value: 'pet-services', label: 'Pet Services & Veterinary', tier: 3 },
  { value: 'pharmaceutical', label: 'Pharmaceutical', tier: 1 },
  { value: 'photography', label: 'Photography & Videography', tier: 3 },
  { value: 'political', label: 'Political Campaigns', tier: 1 },
  { value: 'professional-services', label: 'Professional Services', tier: 3 },
  { value: 'real-estate', label: 'Real Estate', tier: 2 },
  { value: 'recruiting', label: 'Recruiting & Staffing', tier: 2 },
  { value: 'religious', label: 'Religious Organizations', tier: 3 },
  { value: 'saas', label: 'SaaS', tier: 3 },
  { value: 'security', label: 'Security Services', tier: 3 },
  { value: 'sports-fitness', label: 'Sports & Fitness', tier: 3 },
  { value: 'technology', label: 'Technology & Software', tier: 3 },
  { value: 'telecommunications', label: 'Telecommunications', tier: 3 },
  { value: 'travel-hospitality', label: 'Travel & Hospitality', tier: 3 },
  { value: 'weight-loss', label: 'Weight Loss & Fitness', tier: 2 },
];

// Tab configuration
const TAB_IDS = ['overview', 'product', 'brand', 'customers', 'budgetLegacy', 'budget', 'goals', 'context', 'misc'] as const;
type TabId = typeof TAB_IDS[number];

const getTabs = (sellsServices: boolean, testMode: boolean) => {
  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'brand', label: 'Brand' },
    { id: 'product', label: sellsServices ? 'Services' : 'Products' },
    { id: 'customers', label: 'Customers' },
    { id: 'budget', label: 'Budget' },
  ];

  if (testMode) {
    tabs.push({ id: 'goals', label: 'Goals' });
  }

  tabs.push({ id: 'context', label: 'Context' });
  tabs.push({ id: 'misc', label: 'Misc' });

  return tabs;
};

// Voice example copy data - examples for each dimension at each position
const VOICE_EXAMPLES: Record<string, Record<number, string>> = {
  formality: {
    1: "Yo! Ready to shake things up? Let's do this!",
    2: "Hey, let's talk about how we can help you out!",
    3: "Check out what we can do for your business.",
    4: "We're pleased to offer you our range of business solutions.",
    5: "We cordially invite you to explore our comprehensive suite of professional services.",
  },
  humor: {
    1: "Our platform delivers measurable results through data-driven optimization.",
    2: "We help you get better results, and we're pretty good at it.",
    3: "Great results without the headache. That's kind of our thing.",
    4: "Warning: Side effects may include excessive happiness and confused competitors.",
    5: "We put the 'fun' in 'fundamental business transformation'! 🎉",
  },
  respect: {
    1: "Your old way of doing things? Yeah, that's cute. Try this instead.",
    2: "Let's be real: your current approach isn't working. We can fix that.",
    3: "We're here to help you succeed, plain and simple.",
    4: "We appreciate your business and work hard to meet your expectations.",
    5: "We deeply value your trust and are honored to serve your needs.",
  },
  enthusiasm: {
    1: "The data indicates a 23% improvement in conversion metrics.",
    2: "You'll see solid improvements in your key metrics.",
    3: "Get ready for some really good results!",
    4: "You're going to LOVE what this does for your business!",
    5: "THIS IS AMAZING!! 🚀 Your results are about to go through the ROOF!!",
  },
  technicality: {
    1: "More clicks. More sales. Simple as that.",
    2: "We make your ads work smarter so you get more customers.",
    3: "Smart technology that helps your ads perform better.",
    4: "Our AI analyzes patterns to optimize your return on ad spend.",
    5: "Leverage our ML-powered NLP algorithms for semantic optimization of your ROAS.",
  },
};

// Brand profile generation loading carousel
const LOADING_STEPS = [
  {
    icon: Globe,
    title: 'Analyzing Your Website...',
    description: 'Reading your content to understand your business',
  },
  {
    icon: Package,
    title: 'Discovering Products and Services...',
    description: 'Identifying what you offer to customers',
  },
  {
    icon: Gem,
    title: 'Extracting Value Proposition...',
    description: 'Understanding what makes your business unique',
  },
  {
    icon: Briefcase,
    title: 'Researching Your Industry...',
    description: 'Categorizing your market and positioning',
  },
  {
    icon: UserCircle,
    title: 'Identifying Your Customers...',
    description: 'Building ideal customer profiles based on your business',
  },
  {
    icon: Shield,
    title: 'Discovering Trust Signals...',
    description: 'Finding what builds credibility with your audience',
  },
  {
    icon: Palette,
    title: 'Defining Your Brand Voice...',
    description: 'Understanding your tone, style, and messaging',
  },
];

function BrandProfileLoadingCarousel() {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % LOADING_STEPS.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const step = LOADING_STEPS[currentStep];
  const StepIcon = step.icon;

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
      {/* Icon with spinner */}
      <div className="relative mb-4">
        <StepIcon className="h-12 w-12 text-gray-500" strokeWidth={1} />
        <Spinner size="sm" className="text-[#290BD9] absolute -bottom-1 -right-1" />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-500 mb-2">
        {step.title}
      </h3>

      {/* Description */}
      <p className="text-gray-500 mb-6">
        {step.description}
      </p>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-4">
        {LOADING_STEPS.map((_, index) => (
          <div
            key={index}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === currentStep
                ? 'w-6 bg-[#290BD9]'
                : 'w-1.5 bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Time estimate */}
      <p className="text-gray-500">
        Usually takes 1-2 minutes
      </p>
    </div>
  );
}

// Interactive voice slider component with integrated copy preview
function VoiceSlider({
  leftLabel,
  rightLabel,
  value,
  savedValue,
  dimension,
  onChange
}: {
  leftLabel: string;
  rightLabel: string;
  value: number;
  savedValue: number;
  dimension: keyof typeof VOICE_EXAMPLES;
  onChange: (value: number) => void;
}) {
  // Convert 1-5 scale to 0-100 percentage for Slider component
  const percentage = ((value - 1) / 4) * 100;
  const savedPercentage = ((savedValue - 1) / 4) * 100;
  const exampleCopy = VOICE_EXAMPLES[dimension]?.[value] || '';

  const handleSliderChange = (newPercentage: number) => {
    // Map 0-100 back to 1-5, snapping to discrete values
    const newValue = Math.round((newPercentage / 100) * 4) + 1;
    const clampedValue = Math.max(1, Math.min(5, newValue));
    onChange(clampedValue);
  };

  return (
    <div className="mb-4">
      {/* Labels - Caption style: text-sm font-medium text-gray-700 */}
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-sm font-medium text-gray-700">{leftLabel}</span>
        <span className="text-sm font-medium text-gray-700">{rightLabel}</span>
      </div>
      <Slider
        value={percentage}
        referenceValue={savedPercentage}
        onChange={handleSliderChange}
        step={25}
      />
      {/* Copy preview - Small style: text-xs font-normal text-gray-500 */}
      <p className="text-xs font-normal text-gray-500 mt-1.5 truncate" title={exampleCopy}>"{exampleCopy}"</p>
    </div>
  );
}


// Differentiator categories with all options
const DIFFERENTIATOR_CATEGORIES = [
  {
    name: 'What We Deliver',
    options: ['Higher Quality', 'Better Design', 'More Reliable', 'More Innovative', 'More Customized'],
  },
  {
    name: 'Price & Value',
    options: ['Lower Cost', 'Better Value', 'More Premium'],
  },
  {
    name: 'Speed & Ease',
    options: ['Faster', 'Easier', 'More Convenient', 'More Available'],
  },
  {
    name: 'Service & Care',
    options: ['Better Service', 'More Personal', 'More Caring'],
  },
  {
    name: 'Trust & Credibility',
    options: ['More Trusted', 'More Expert', 'More Established', 'More Authentic', 'Safer'],
  },
  {
    name: 'Values & Purpose',
    options: ['More Sustainable', 'More Ethical', 'More Local'],
  },
  {
    name: 'Experience',
    options: ['More Fun', 'More Exclusive'],
  },
];

// Archetype data - 12 brand archetypes with full details for selection cards
const ARCHETYPES = [
  { id: 'Innocent', name: 'Innocent', icon: '😇', trait: 'Optimistic & Pure', fullTrait: 'Optimistic, trusting, pure', examples: 'Coca-Cola, Dove, Whole Foods', desc: 'Innocent brands promise simplicity and happiness. They offer straightforward solutions and evoke nostalgia, purity, and optimism.' },
  { id: 'Everyman', name: 'Everyman', icon: '👤', trait: 'Relatable & Humble', fullTrait: 'Relatable, down-to-earth, humble', examples: 'IKEA, Target, Budweiser', desc: 'Everyman brands connect through common virtues and relatability. They\'re friendly, humble, and make customers feel like they belong.' },
  { id: 'Hero', name: 'Hero', icon: '🏆', trait: 'Courageous & Strong', fullTrait: 'Courageous, determined, inspiring', examples: 'Nike, FedEx, BMW', desc: 'Hero brands inspire customers to become the best version of themselves. They believe in hard work, taking risks, and overcoming challenges.' },
  { id: 'Outlaw', name: 'Outlaw', icon: '🔥', trait: 'Rebellious & Bold', fullTrait: 'Rebellious, disruptive, bold', examples: 'Harley-Davidson, Virgin, Diesel', desc: 'Outlaw brands break the rules and challenge the status quo. They appeal to those who feel alienated or want to overturn what\'s not working.' },
  { id: 'Explorer', name: 'Explorer', icon: '🧭', trait: 'Adventurous & Free', fullTrait: 'Adventurous, curious, free', examples: 'Jeep, Patagonia, REI', desc: 'Explorer brands inspire customers to discover new experiences and push boundaries. They value freedom, authenticity, and self-discovery.' },
  { id: 'Creator', name: 'Creator', icon: '🎨', trait: 'Innovative & Artistic', fullTrait: 'Imaginative, innovative, artistic', examples: 'Adobe, Lego, Pinterest', desc: 'Creator brands foster self-expression and bring ideas to life. They value originality, imagination, and turning vision into reality.' },
  { id: 'Ruler', name: 'Ruler', icon: '👑', trait: 'Authoritative & Elite', fullTrait: 'Authoritative, confident, elite', examples: 'Rolex, Mercedes-Benz, Amex', desc: 'Ruler brands exude power, prestige, and success. They help customers feel in control and part of an exclusive, successful group.' },
  { id: 'Magician', name: 'Magician', icon: '🔮', trait: 'Visionary & Transformative', fullTrait: 'Visionary, transformative, innovative', examples: 'Disney, Apple, Tesla', desc: 'Magician brands promise transformation and make dreams come true. They create magical experiences and turn problems into opportunities.' },
  { id: 'Lover', name: 'Lover', icon: '❤️', trait: 'Passionate & Sensual', fullTrait: 'Passionate, intimate, sensual', examples: 'Victoria\'s Secret, Chanel, Godiva', desc: 'Lover brands inspire passion, intimacy, and sensual pleasure. They help people feel special, attractive, and worthy of love.' },
  { id: 'Caregiver', name: 'Caregiver', icon: '🤝', trait: 'Nurturing & Supportive', fullTrait: 'Nurturing, compassionate, supportive', examples: 'Johnson & Johnson, TOMS, Volvo', desc: 'Caregiver brands protect and care for others. They are generous, compassionate, and driven by a desire to help people feel supported.' },
  { id: 'Jester', name: 'Jester', icon: '🃏', trait: 'Playful & Fun', fullTrait: 'Playful, humorous, lighthearted', examples: 'Old Spice, Ben & Jerry\'s, M&Ms', desc: 'Jester brands bring joy and lightheartedness. They don\'t take themselves too seriously and help customers live in the moment.' },
  { id: 'Sage', name: 'Sage', icon: '📚', trait: 'Wise & Analytical', fullTrait: 'Wise, analytical, thoughtful', examples: 'Google, BBC, Harvard', desc: 'Sage brands are trusted sources of knowledge and expertise. They help customers understand the world and make informed decisions.' },
];

// Persona type - represents a buyer persona (the individual who makes purchase decisions)
interface Persona {
  id: string;
  name: string;
  type: 'B2B' | 'B2C';  // Determines which fields to show (firmographics vs demographics)
  // Firmographics (B2B personas)
  jobTitle?: string;
  industries?: string[];
  companySize?: string;
  revenue?: string;
  // Demographics (B2C personas)
  ageRange?: string;
  gender?: string;
  incomeRange?: string;
  // Psychographics (all personas)
  painPoints: string[];
  goals: string[];
  objections: string[];
  buyingTriggers: string[];
  // Relationships
  linkedProductIds: string[];
}

// Product type - represents what the business sells
interface Product {
  id: string;
  name: string;
  description: string;
  marketTypes: ('B2B' | 'B2C')[];  // Can target multiple markets
  features: string[];  // Capabilities of this product (not separate products)
  landingPageUrl?: string;
  linkedPersonaIds: string[];
}


// Empty brand identity (no fake data)
const EMPTY_BRAND_IDENTITY: BrandIdentity = {
  archetypes: {
    primary: '',
    secondary: '',
  },
  voice: {
    formality: 3,
    humor: 3,
    respect: 3,
    enthusiasm: 3,
    technicality: 3,
  },
  differentiators: [],
};

// Empty settings (no fake data)
const EMPTY_SETTINGS: BusinessSettings = {
  sells_services: false,
  brand_adherence_mode: 'strict',
  llm_instructions: '',
};

// Default platform budgets
const DEFAULT_PLATFORM_BUDGETS: PlatformBudget[] = [
  { platform: 'Google', amount: 0 },
  { platform: 'Meta', amount: 0 },
  { platform: 'LinkedIn', amount: 0 },
];

// Helper: Convert backend ICP to UI Persona format
function icpToPersona(icp: Icp, productIcpLinks: ProductIcpLink[]): Persona {
  const linkedProductIds = productIcpLinks
    .filter(link => link.icp_id === icp.id)
    .map(link => link.product_id);

  return {
    id: icp.id,
    name: icp.name,
    type: icp.market_types?.includes('B2B') ? 'B2B' : 'B2C',
    jobTitle: icp.title,
    industries: icp.firmographics.industries,
    companySize: icp.firmographics.company_size,
    revenue: icp.firmographics.revenue,
    ageRange: icp.demographics.age_range,
    gender: icp.demographics.gender,
    incomeRange: icp.demographics.income_range,
    painPoints: icp.pgob.pains,
    goals: icp.pgob.goals,
    objections: icp.pgob.objections,
    buyingTriggers: icp.pgob.buying_triggers,
    linkedProductIds,
  };
}

// Helper: Convert backend ProductData to UI Product format
function productDataToProduct(product: ProductData, productIcpLinks: ProductIcpLink[]): Product {
  const linkedPersonaIds = productIcpLinks
    .filter(link => link.product_id === product.id)
    .map(link => link.icp_id);

  return {
    id: product.id,
    name: product.name,
    description: product.description,
    marketTypes: (product.market_types || ['B2B']).filter(t => t !== 'B2B2C') as ('B2B' | 'B2C')[],
    features: product.features || [],
    landingPageUrl: product.landing_page_url || undefined,
    linkedPersonaIds,
  };
}

// Helper: Get personas from business data (no mock fallback to preserve empty state)
function getPersonasFromBusiness(business: Business | null): Persona[] {
  if (!business) {
    return [];
  }
  if (!business.icps || business.icps.length === 0) {
    return [];
  }
  const links = business.product_icp_links || [];
  return business.icps.map(icp => icpToPersona(icp, links));
}

// Helper: Get products from business data (no mock fallback to preserve empty state)
function getProductsFromBusiness(business: Business | null): Product[] {
  if (!business) {
    return [];
  }
  if (!business.products_data || business.products_data.length === 0) {
    return [];
  }
  const links = business.product_icp_links || [];
  return business.products_data.map(product => productDataToProduct(product, links));
}

// Helper: Get brand identity from business (empty if not set)
function getBrandIdentityFromBusiness(business: Business | null): BrandIdentity {
  return business?.brand_identity || EMPTY_BRAND_IDENTITY;
}

// Helper: Get settings from business (empty if not set)
function getSettingsFromBusiness(business: Business | null): BusinessSettings {
  return business?.settings || EMPTY_SETTINGS;
}

// Helper: Get platform budgets from business or fallback to default
function getPlatformBudgetsFromBusiness(business: Business | null): { google: number; meta: number; linkedin: number } {
  const budgets = business?.platform_budgets || DEFAULT_PLATFORM_BUDGETS;
  return {
    google: budgets.find(b => b.platform === 'Google')?.amount || 0,
    meta: budgets.find(b => b.platform === 'Meta')?.amount || 0,
    linkedin: budgets.find(b => b.platform === 'LinkedIn')?.amount || 0,
  };
}

// Helper: Get trust signals from business or fallback to empty
function getTrustSignalsFromBusiness(business: Business | null): TrustSignal[] {
  if (!business?.trust_signals || business.trust_signals.length === 0) {
    return [];
  }
  return business.trust_signals.map((signal, idx) => ({
    id: `ts-${idx}`,
    name: signal,
  }));
}

// Helper: Get industry from business
function getIndustryFromBusiness(business: Business | null): string {
  return business?.industry?.label || '';
}

// Differentiator chip component
function DifferentiatorChip({ label, isSelected, onClick }: { label: string; isSelected: boolean; onClick?: () => void }) {
  return (
    <span
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full text-sm font-medium border-2 transition-colors ${
        isSelected
          ? 'bg-[#290BD9] text-white border-[#290BD9]'
          : 'bg-white text-gray-500 border-gray-200'
      } ${onClick ? 'cursor-pointer hover:border-gray-300' : ''}`}
    >
      {isSelected && (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {label}
    </span>
  );
}

// Helper to get linked personas for a product
function getLinkedPersonas(product: Product, allPersonas: Persona[]): Persona[] {
  return allPersonas.filter(p => product.linkedPersonaIds.includes(p.id));
}

// Helper to get linked products for a persona
function getLinkedProducts(persona: Persona, allProducts: Product[]): Product[] {
  return allProducts.filter(p => persona.linkedProductIds.includes(p.id));
}

// Editable Product detail panel (right side of Products tab)
function ProductDetailEditable({
  product,
  allPersonas,
  onUpdate,
  onDelete,
  onLinkPersona,
  onUnlinkPersona,
  onNavigateToPersona,
}: {
  product: Product;
  allPersonas: Persona[];
  onUpdate: (product: Product) => void;
  onDelete: (productId: string) => void;
  onLinkPersona: (productId: string, personaId: string) => void;
  onUnlinkPersona: (productId: string, personaId: string) => void;
  onNavigateToPersona: (personaId: string) => void;
}) {
  const linkedPersonas = getLinkedPersonas(product, allPersonas);
  const unlinkedPersonas = allPersonas.filter(p => !product.linkedPersonaIds.includes(p.id));
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Input
          value={product.name}
          onChange={(e) => onUpdate({ ...product, name: e.target.value })}
          className="text-lg font-semibold bg-white flex-1"
          placeholder="Product name"
        />
        <Button
          variant="ghost"
          size="icon"
          className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-10 w-10"
          onClick={() => onDelete(product.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Sells to */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Sells to</h4>
        <div className="flex items-center gap-3">
          <label className="flex items-center space-x-2 h-10 pl-3 pr-6 rounded-md border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
            <Checkbox
              id={`${product.id}-b2b`}
              checked={product.marketTypes.includes('B2B')}
              onCheckedChange={(checked) => {
                const newTypes = checked
                  ? [...product.marketTypes, 'B2B' as const]
                  : product.marketTypes.filter(t => t !== 'B2B');
                onUpdate({ ...product, marketTypes: newTypes });
              }}
            />
            <span className="text-sm text-gray-900">Businesses</span>
          </label>
          <label className="flex items-center space-x-2 h-10 pl-3 pr-6 rounded-md border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
            <Checkbox
              id={`${product.id}-b2c`}
              checked={product.marketTypes.includes('B2C')}
              onCheckedChange={(checked) => {
                const newTypes = checked
                  ? [...product.marketTypes, 'B2C' as const]
                  : product.marketTypes.filter(t => t !== 'B2C');
                onUpdate({ ...product, marketTypes: newTypes });
              }}
            />
            <span className="text-sm text-gray-900">Consumers</span>
          </label>
        </div>
      </div>

      {/* Description */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Description</h4>
        <Textarea
          value={product.description}
          onChange={(e) => onUpdate({ ...product, description: e.target.value })}
          className="bg-white min-h-[130px]"
          placeholder="Describe this product or service..."
        />
      </div>

      {/* Features */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Features</h4>
        <div className="space-y-2">
          {product.features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={feature}
                onChange={(e) => {
                  const next = [...product.features];
                  next[idx] = e.target.value;
                  onUpdate({ ...product, features: next });
                }}
                className="bg-white flex-1"
                placeholder="A capability of this product"
              />
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-9 w-9 flex-shrink-0"
                onClick={() => onUpdate({ ...product, features: product.features.filter((_, i) => i !== idx) })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <button
            onClick={() => onUpdate({ ...product, features: [...product.features, ''] })}
            className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full text-sm font-medium border-2 border-dashed border-gray-300 text-gray-500 hover:border-[#290BD9] hover:text-[#290BD9] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add Feature
          </button>
        </div>
      </div>

      {/* Landing Page */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Landing Page URL</h4>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <BusinessLandingPageCombobox
              selectedUrl={product.landingPageUrl ?? null}
              onSelect={(entry) => onUpdate({ ...product, landingPageUrl: entry?.url ?? undefined })}
              placeholder="Select or add a landing page..."
            />
          </div>
          {product.landingPageUrl && (
            <a
              href={product.landingPageUrl.startsWith('http') ? product.landingPageUrl : `https://${product.landingPageUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-[#290BD9] transition-colors flex-shrink-0"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>

      {/* Ideal Customers */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
          Ideal Customers ({linkedPersonas.length})
        </h4>
        {linkedPersonas.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {linkedPersonas.map((persona) => (
              <div
                key={persona.id}
                onClick={() => onNavigateToPersona(persona.id)}
                className="border border-gray-200 rounded-lg p-3 bg-white hover:bg-gray-50 hover:border-[#290BD9] transition-colors group relative cursor-pointer"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnlinkPersona(product.id, persona.id);
                  }}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-6 w-6 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                    <User className="h-3 w-3 text-[#290BD9]" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{persona.name}</span>
                </div>
                <div className="text-sm text-gray-500 ml-8">
                  {persona.type === 'B2B' ? persona.jobTitle : persona.ageRange}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No customers linked</p>
        )}
        {unlinkedPersonas.length > 0 && (
          <button
            onClick={() => setShowLinkDialog(true)}
            className="mt-3 inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full text-sm font-medium border-2 border-dashed border-gray-300 text-gray-500 hover:border-[#290BD9] hover:text-[#290BD9] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Link Customer
          </button>
        )}

        {/* Link Customer Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Link Customer to {product.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {unlinkedPersonas.map((persona) => (
                <button
                  key={persona.id}
                  onClick={() => {
                    onLinkPersona(product.id, persona.id);
                    setShowLinkDialog(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-[#290BD9] hover:bg-[#290BD9]/5 transition-colors text-left"
                >
                  <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                    <User className="h-4 w-4 text-[#290BD9]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{persona.name}</div>
                    <div className="text-xs text-gray-500">
                      {persona.type} · {persona.type === 'B2B' ? persona.jobTitle : persona.ageRange}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Editable List Item component for PGOB fields and trust signals
function EditableListItem({
  value,
  onUpdate,
  onDelete,
  placeholder,
  maxLength,
  itemLabel = 'item',
}: {
  value: string;
  onUpdate: (newValue: string) => void;
  onDelete: () => void;
  placeholder: string;
  maxLength?: number;
  itemLabel?: string;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteConfirm = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <EditableField
        value={value}
        onChange={onUpdate}
        onDelete={() => setShowDeleteConfirm(true)}
        placeholder={placeholder}
        maxLength={maxLength}
      />
      <ConformationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title={`Remove ${itemLabel}`}
        description={`Are you sure you want to remove "${value || 'this item'}"? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />
    </>
  );
}

// Editable Persona detail panel (right side of Audiences tab)
function PersonaDetailEditable({
  persona,
  allProducts,
  onUpdate,
  onDelete,
  onLinkProduct,
  onUnlinkProduct,
  onNavigateToProduct,
  sellsServices = false,
}: {
  persona: Persona;
  allProducts: Product[];
  onUpdate: (persona: Persona) => void;
  onDelete: (personaId: string) => void;
  onLinkProduct: (personaId: string, productId: string) => void;
  onUnlinkProduct: (personaId: string, productId: string) => void;
  onNavigateToProduct: (productId: string) => void;
  sellsServices?: boolean;
}) {
  const linkedProducts = getLinkedProducts(persona, allProducts);
  const unlinkedProducts = allProducts.filter(p => !persona.linkedProductIds.includes(p.id));
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const updateListItem = (field: 'painPoints' | 'goals' | 'objections' | 'buyingTriggers', index: number, value: string) => {
    const newList = [...persona[field]];
    newList[index] = value;
    onUpdate({ ...persona, [field]: newList });
  };

  const deleteListItem = (field: 'painPoints' | 'goals' | 'objections' | 'buyingTriggers', index: number) => {
    const newList = persona[field].filter((_, i) => i !== index);
    onUpdate({ ...persona, [field]: newList });
  };

  const addListItem = (field: 'painPoints' | 'goals' | 'objections' | 'buyingTriggers') => {
    onUpdate({ ...persona, [field]: [...persona[field], ''] });
  };

  return (
    <div className="space-y-5">
      {/* Header with delete button */}
      <div className="space-y-3">
        <div>
          <div className="text-xs text-gray-500 mb-1">Customer</div>
          <div className="flex items-center gap-2">
            <Input
              value={persona.name}
              onChange={(e) => onUpdate({ ...persona, name: e.target.value })}
              className="bg-white flex-1"
              placeholder={persona.type === 'B2B' ? 'e.g. Tech Startups' : 'e.g. Young Professionals'}
            />
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-10 w-10"
              onClick={() => onDelete(persona.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-gray-500 mb-1">Customer Type</div>
            <Select
              value={persona.type}
              onValueChange={(value: 'B2B' | 'B2C') => onUpdate({ ...persona, type: value })}
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="B2B">B2B (Business)</SelectItem>
                <SelectItem value="B2C">B2C (Consumer)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {persona.type === 'B2B' && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Decision Maker</div>
              <Input
                value={persona.jobTitle || ''}
                onChange={(e) => onUpdate({ ...persona, jobTitle: e.target.value })}
                className="bg-white"
                placeholder="e.g. Marketing Director"
              />
            </div>
          )}
        </div>
      </div>

      {/* Firmographics (B2B) or Demographics (B2C) */}
      {persona.type === 'B2B' ? (
        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Firmographics</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-500 mb-1">Company Size</div>
              <Input
                value={persona.companySize || ''}
                onChange={(e) => onUpdate({ ...persona, companySize: e.target.value })}
                className="bg-white"
                placeholder="e.g. 1-50 employees"
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Revenue</div>
              <Input
                value={persona.revenue || ''}
                onChange={(e) => onUpdate({ ...persona, revenue: e.target.value })}
                className="bg-white"
                placeholder="e.g. $500K - $5M"
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Demographics</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-500 mb-1">Age Range</div>
              <Input
                value={persona.ageRange || ''}
                onChange={(e) => onUpdate({ ...persona, ageRange: e.target.value })}
                className="bg-white"
                placeholder="e.g. 25-45"
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Gender</div>
              <Input
                value={persona.gender || ''}
                onChange={(e) => onUpdate({ ...persona, gender: e.target.value })}
                className="bg-white"
                placeholder="e.g. All, Male, Female"
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Income Range</div>
              <Input
                value={persona.incomeRange || ''}
                onChange={(e) => onUpdate({ ...persona, incomeRange: e.target.value })}
                className="bg-white"
                placeholder="e.g. $50K - $100K"
              />
            </div>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Pain Points & Goals - 2 column */}
      <div className="grid grid-cols-2 gap-6">
        <div className="group/section">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Pain Points</h4>
            <button
              onClick={() => addListItem('painPoints')}
              className="text-xs text-gray-500 hover:text-[#290BD9] opacity-0 group-hover/section:opacity-100 transition-opacity"
            >
              Add
            </button>
          </div>
          <div className="space-y-0">
            {persona.painPoints.map((point, idx) => (
              <EditableListItem
                key={idx}
                value={point}
                onUpdate={(v) => updateListItem('painPoints', idx, v)}
                onDelete={() => deleteListItem('painPoints', idx)}
                placeholder="Pain point..."
                itemLabel="pain point"
              />
            ))}
          </div>
        </div>

        <div className="group/section">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Goals</h4>
            <button
              onClick={() => addListItem('goals')}
              className="text-xs text-gray-500 hover:text-[#290BD9] opacity-0 group-hover/section:opacity-100 transition-opacity"
            >
              Add
            </button>
          </div>
          <div className="space-y-0">
            {persona.goals.map((goal, idx) => (
              <EditableListItem
                key={idx}
                value={goal}
                onUpdate={(v) => updateListItem('goals', idx, v)}
                onDelete={() => deleteListItem('goals', idx)}
                placeholder="Goal..."
                itemLabel="goal"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Objections & Buying Triggers - 2 column */}
      <div className="grid grid-cols-2 gap-6">
        <div className="group/section">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Objections</h4>
            <button
              onClick={() => addListItem('objections')}
              className="text-xs text-gray-500 hover:text-[#290BD9] opacity-0 group-hover/section:opacity-100 transition-opacity"
            >
              Add
            </button>
          </div>
          <div className="space-y-0">
            {persona.objections.map((objection, idx) => (
              <EditableListItem
                key={idx}
                value={objection}
                onUpdate={(v) => updateListItem('objections', idx, v)}
                onDelete={() => deleteListItem('objections', idx)}
                placeholder="Objection..."
                itemLabel="objection"
              />
            ))}
          </div>
        </div>

        <div className="group/section">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Buying Triggers</h4>
            <button
              onClick={() => addListItem('buyingTriggers')}
              className="text-xs text-gray-500 hover:text-[#290BD9] opacity-0 group-hover/section:opacity-100 transition-opacity"
            >
              Add
            </button>
          </div>
          <div className="space-y-0">
            {persona.buyingTriggers.map((trigger, idx) => (
              <EditableListItem
                key={idx}
                value={trigger}
                onUpdate={(v) => updateListItem('buyingTriggers', idx, v)}
                onDelete={() => deleteListItem('buyingTriggers', idx)}
                placeholder="Buying trigger..."
                itemLabel="buying trigger"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Linked Products/Services */}
      <div>
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
          {sellsServices ? 'Services' : 'Products'} This Customer Buys ({linkedProducts.length})
        </h4>
        {linkedProducts.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {linkedProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => onNavigateToProduct(product.id)}
                className="border border-gray-200 rounded-lg p-3 bg-white hover:bg-gray-50 hover:border-[#290BD9] transition-colors group relative cursor-pointer"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnlinkProduct(persona.id, product.id);
                  }}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center">
                    <Package className="h-3 w-3 text-gray-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{product.name}</span>
                </div>
                <div className="text-sm text-gray-500 ml-8">
{product.marketTypes.join(' · ') || 'No market type'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No {sellsServices ? 'services' : 'products'} linked</p>
        )}
        {unlinkedProducts.length > 0 && (
          <button
            onClick={() => setShowLinkDialog(true)}
            className="mt-3 inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full text-sm font-medium border-2 border-dashed border-gray-300 text-gray-500 hover:border-[#290BD9] hover:text-[#290BD9] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Link {sellsServices ? 'Service' : 'Product'}
          </button>
        )}

        {/* Link Product Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Link {sellsServices ? 'Service' : 'Product'} to {persona.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {unlinkedProducts.map((product) => (
                <button
                  key={product.id}
                  onClick={() => {
                    onLinkProduct(persona.id, product.id);
                    setShowLinkDialog(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-[#290BD9] hover:bg-[#290BD9]/5 transition-colors text-left"
                >
                  <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <Package className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                    <div className="text-xs text-gray-500">{product.marketTypes.join(' · ') || 'No market type'}</div>
                  </div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Campaign budget allocation state
interface CampaignBudgetAllocation {
  campaignId: number;
  budget: number;
  isLocked: boolean;
}

// Parse campaign name to extract display name only (same as campaign-sidebar)
const parseCampaignName = (name: string) => {
  const parts = name.split(' | ');
  if (parts.length >= 3 && parts[0] === 'Glitch') {
    if (parts.length === 4) {
      if (parts[1] === 'Geo') {
        return `${parts[3]} | ${parts[2]}`;
      }
      return parts[3];
    } else {
      return parts[2];
    }
  }
  return name;
};

// Get campaign type display — delegates to shared util

const getCampaignTypeStyle = (goal: string | undefined) => {
  switch (goal) {
    case 'brand': return 'bg-purple-100 text-purple-800';
    case 'competitor': return 'bg-orange-100 text-orange-800';
    case 'prospecting': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Budget slider row component for campaign allocation (used in budget tab - temporarily disabled)
function _BudgetSliderRow({
  campaign,
  allocation,
  sliderMax,
  currencySymbol,
  onBudgetChange,
  onLockToggle,
  onDragStart,
  onDragEnd
}: {
  campaign: CampaignData;
  allocation: CampaignBudgetAllocation;
  sliderMax: number;
  currencySymbol: string;
  onBudgetChange: (campaignId: number, budget: number) => void;
  onLockToggle: (campaignId: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const [inputValue, setInputValue] = useState(allocation.budget.toLocaleString());
  const [, setIsDragging] = useState(false);

  // Sync input value when allocation changes externally
  useEffect(() => {
    setInputValue(allocation.budget.toLocaleString());
  }, [allocation.budget]);

  // Calculate percentage position on slider (clamped to 0-100)
  const percentage = sliderMax > 0 ? Math.min((allocation.budget / sliderMax) * 100, 100) : 0;

  // Google recommended budget - use campaign's original budget * 1.15 as fake placeholder
  // This is static and doesn't change when user drags the slider
  const googleRecommendedBudget = (campaign.budget || 0) * 1.15;
  const googleRecommendedPercentage = sliderMax > 0 ? Math.min((googleRecommendedBudget / sliderMax) * 100, 100) : 0;

  const handleDragStart = () => {
    setIsDragging(true);
    onDragStart();
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (allocation.isLocked) {
      setInputValue(allocation.budget.toLocaleString());
      return;
    }
    const value = parseFloat(inputValue.replace(/,/g, '')) || 0;
    onBudgetChange(campaign.id, Math.max(0, value));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const displayName = parseCampaignName(campaign.name);

  return (
    <div className="grid grid-cols-[180px_100px_1fr_120px_40px] gap-4 items-center py-3 border-b border-gray-100 last:border-b-0">
      {/* Campaign name */}
      <div className="min-w-0">
        <span className="text-sm font-medium text-gray-900 truncate block">{displayName}</span>
      </div>

      {/* Campaign type badge - in its own column for alignment */}
      <div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCampaignTypeStyle(campaign.goal)}`}>
          {getCampaignTypeLabel(campaign.goal)}
        </span>
      </div>

      {/* Slider */}
      <div className="px-2">
        <Slider
          value={percentage}
          referenceValue={googleRecommendedPercentage}
          onChange={(value) => {
            if (allocation.isLocked) return;
            const newBudget = Math.round((value / 100) * sliderMax);
            onBudgetChange(campaign.id, newBudget);
          }}
          disabled={allocation.isLocked}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        />
      </div>

      {/* Budget input with border */}
      <div className="flex items-center">
        <div className={`flex items-center gap-1 px-2 py-1.5 border rounded-md transition-colors ${allocation.isLocked ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300 focus-within:border-[#290BD9]'}`}>
          <span className="text-sm text-gray-500">{currencySymbol}</span>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            onFocus={(e) => e.target.select()}
            disabled={allocation.isLocked}
            className={`text-sm font-medium text-right bg-transparent border-none outline-none w-16 focus:ring-0 p-0 ${allocation.isLocked ? 'text-gray-500' : 'text-gray-900'}`}
          />
        </div>
      </div>

      {/* Lock button */}
      <div className="flex justify-center">
        <button
          onClick={() => onLockToggle(campaign.id)}
          className={`p-1.5 rounded-md transition-colors ${allocation.isLocked ? 'text-[#290BD9] bg-[#290BD9]/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
        >
          {allocation.isLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

// #5546: default "Additional AI Instructions" shown as the field placeholder. The saved value stays
// empty until the user types; the backend applies this same text as CUSTOM INSTRUCTIONS when the
// saved value is empty. KEEP IN SYNC with DEFAULT_AI_INSTRUCTIONS in backend/app/pipeline/headlines.py.
const DEFAULT_AI_INSTRUCTIONS =
  'Write clear, benefit-led ad copy in a professional, trustworthy tone. Lead with the core value ' +
  'proposition and a concrete customer outcome. Use plain language: avoid jargon, hype, and ' +
  'unverifiable claims. Prefer specifics over vague adjectives. Include a natural call to action, ' +
  'keep it concise and skimmable, and never name or compare against competitors.';

function BusinessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setManualBrandEntry = useSetAtom(manualBrandEntryAtom);
  const allCampaigns = useAtomValue(campaignsDataAtom);
  const budgetNeedsAttention = !organizationData?.max_spend && allCampaigns.some(c => (c.budget || 0) > 0);
  const { exportToPDF, isExporting, progress, stage } = useBusinessPDFExport();
  const testMode = useTestMode();
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [personaToDelete, setPersonaToDelete] = useState<Persona | null>(null);
  // Add-via-modal state (standard toolbar-button + modal pattern). The created item is appended to
  // local editable state and selected for inline editing; persistence is via the page's Save flow.
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [personaModalOpen, setPersonaModalOpen] = useState(false);
  const [sellsServices, setSellsServices] = useState(false); // false = Products, true = Services
  const [discoverMode, setDiscoverMode] = useState(false); // false = Strict, true = Discovery
  const [industryOpen, setIndustryOpen] = useState(false);
  const [industryValue, setIndustryValue] = useState('');
  const [savedIndustryValue, setSavedIndustryValue] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [savedBusinessName, setSavedBusinessName] = useState('');
  const { symbol: currencySymbolRaw } = useCurrencySymbol();
  const _currencySymbol = currencySymbolRaw ?? ''; // Prefixed - used in budget tab (temporarily disabled)


  // Trust signals state
  const [trustSignals, setTrustSignals] = useState<TrustSignal[]>([]);
  const [savedTrustSignals, setSavedTrustSignals] = useState<TrustSignal[]>([]);

  // Campaign budget allocation state
  const [campaignBudgets, setCampaignBudgets] = useState<CampaignBudgetAllocation[]>([]);

  // Platform budget state - bidirectionally synced with total
  const [platformBudgets, setPlatformBudgets] = useState({
    google: 0,
    meta: 0,
    linkedin: 0,
  });
  const [savedPlatformBudgets, setSavedPlatformBudgets] = useState({
    google: 0,
    meta: 0,
    linkedin: 0,
  });
  const [_totalBudget, _setTotalBudget] = useState(0); // Prefixed - used in budget tab (temporarily disabled)
  const [_isEditingTotal, _setIsEditingTotal] = useState(false); // Prefixed - used in budget tab (temporarily disabled)
  const [_hasPlatformAnimated, _setHasPlatformAnimated] = useState(false); // Prefixed - used in budget tab (temporarily disabled)

  // Goals tab is test-mode only — exclude it from validTabs when not in test mode so the
  // hook falls back to 'overview' if a stale atom or hash points at it.
  const validTabs = (testMode ? TAB_IDS : TAB_IDS.filter(t => t !== 'goals')) as TabId[];
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedBusinessTabAtom,
    validTabs,
    'overview',
    (tabId, replace) => navigate({ to: '/business', hash: tabId, replace }),
    location.hash,
  );

  // Fetch business data for the current organization (1:1 relationship)
  // NOTE: Using useQuery (not useSuspenseQuery) because useSuspenseQuery ignores `enabled` option
  // This prevents the 404 error when org ID is 0/undefined before organization data loads
  const {
    data: business,
    isLoading: isBusinessesLoading,
    isError: isBusinessesError,
    refetch: refetchBusiness,
  } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Derive personas and products from business data
  const personas = useMemo(() => getPersonasFromBusiness(business ?? null), [business]);
  const products = useMemo(() => getProductsFromBusiness(business ?? null), [business]);
  const brandIdentity = useMemo(() => getBrandIdentityFromBusiness(business ?? null), [business]);
  const businessSettings = useMemo(() => getSettingsFromBusiness(business ?? null), [business]);

  // Get tabs based on sellsServices state
  const tabs = getTabs(sellsServices, testMode);

  // Business update mutation
  const saveBusinessMutation = useSaveBusinessMutation(organizationData?.id || 0);
  const { toast } = useToast();

  // Poll brand intel generation status
  const queryClient = useQueryClient();
  const { data: taskTracking, isLoading: isTaskTrackingLoading, isError: isTaskTrackingError } = useQuery(
    businessTaskTrackingQueryOptions(business?.id)
  );
  const isGeneratingBrandIntel = taskTracking?.is_generating ?? false;

  // Show error state when: tasks have failed, none completed, not currently generating,
  // AND the business doesn't already have brand data (user may have entered data manually)
  const hasBusinessData = !!(business?.brand_identity || business?.products_data?.length || business?.icps?.length || business?.business_desc);
  const hasBrandIntelFailed = !hasBusinessData
    && (taskTracking?.brand_intel_generation.failed ?? 0) > 0
    && (taskTracking?.brand_intel_generation.completed ?? 0) === 0
    && !isGeneratingBrandIntel;

  // Brand intel retry mutation — pass businessId for scoped query invalidation
  const generateBrandIntelMutation = useGenerateBrandIntelMutation(organizationData?.id || 0, business?.id);

  // On-demand AI generation of a single product (#5378) / customer persona (#5377). The backend appends
  // the new item, so success closes the modal and the invalidated business query re-derives the list.
  const generateProductMutation = useGenerateProductMutation(organizationData?.id || 0, business?.id || 0);
  const generatePersonaMutation = useGeneratePersonaMutation(organizationData?.id || 0, business?.id || 0);

  const handleGenerateProduct = (context: string) => {
    generateProductMutation.mutate(context || undefined, { onSuccess: () => setProductModalOpen(false) });
  };
  const handleGeneratePersona = (context: string) => {
    generatePersonaMutation.mutate(context || undefined, { onSuccess: () => setPersonaModalOpen(false) });
  };

  // When brand intel generation completes, refetch business data
  const prevIsGenerating = useRef(false);
  useEffect(() => {
    if (prevIsGenerating.current && !isGeneratingBrandIntel && organizationData?.id) {
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationData.id, 'business'] });
    }
    prevIsGenerating.current = isGeneratingBrandIntel;
  }, [isGeneratingBrandIntel, queryClient, organizationData?.id]);

  // Fetch campaigns for the organization
  const { data: campaigns = [] } = useQuery(
    campaignsQueryOptions(organizationData?.id, googleAdsAccount?.googleads_account_id)
  );

  // Initialize campaign budgets when campaigns load
  useEffect(() => {
    if (campaigns.length > 0 && campaignBudgets.length === 0) {
      setCampaignBudgets(
        campaigns.map(c => ({
          campaignId: c.id,
          budget: c.budget || 0,
          isLocked: false,
        }))
      );
    }
  }, [campaigns, campaignBudgets.length]);

  // Calculate total allocated budget (used in budget tab - temporarily disabled)
  const _totalAllocatedBudget = useMemo(() => {
    return campaignBudgets.reduce((sum, cb) => sum + cb.budget, 0);
  }, [campaignBudgets]);

  // Track saving state for UI feedback
  const [isSaving, setIsSaving] = useState(false);
  // Brief "Saved ✓" confirmation shown to the left of the Save Changes button
  // after a successful save. Auto-clears after 2s. Matches the reference
  // pattern in business/budget-v2.tsx + organizations/index.tsx (#4387).
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isBudgetV2Dirty, setIsBudgetV2Dirty] = useState(false);
  const [hasBudgetValidationError, setHasBudgetValidationError] = useState(false);
  const budgetSaveRef = useRef<(() => Promise<void>) | null>(null);
  const [isKpiTargetsDirty, setIsKpiTargetsDirty] = useState(false);
  const kpiTargetsControlRef = useRef<KpiTargetsSaveControl | null>(null);
  const [isLocationsDirty, setIsLocationsDirty] = useState(false);
  const locationsControlRef = useRef<LocationsSaveControl | null>(null);



  // Handle campaign budget change (used in budget tab)
  const _handleCampaignBudgetChange = (campaignId: number, newBudget: number) => {
    setCampaignBudgets(prev => prev.map(cb =>
      cb.campaignId === campaignId ? { ...cb, budget: newBudget } : cb
    ));
  };

  // Handle lock toggle (used in budget tab - temporarily disabled)
  const _handleLockToggle = (campaignId: number) => {
    setCampaignBudgets(prev => prev.map(cb =>
      cb.campaignId === campaignId ? { ...cb, isLocked: !cb.isLocked } : cb
    ));
  };

  // Track dragging state for slider scale stability (used in budget tab - temporarily disabled)
  const [_isDragging, _setIsDragging] = useState(false);
  const [_stableSliderMax, _setStableSliderMax] = useState(100);

  // Calculate the current max based on highest budget + 50%
  const calculatedSliderMax = useMemo(() => {
    const highestBudget = Math.max(...campaignBudgets.map(cb => cb.budget), 0);
    return Math.ceil(highestBudget * 1.5) || 100;
  }, [campaignBudgets]);

  // Only update stable max when not dragging (used in budget tab - temporarily disabled)
  useEffect(() => {
    if (!_isDragging) {
      _setStableSliderMax(calculatedSliderMax);
    }
  }, [calculatedSliderMax, _isDragging]);

  // Handler for drag start (used in budget tab - temporarily disabled)
  const _handleDragStart = useCallback(() => {
    _setIsDragging(true);
  }, []);

  // Handler for drag end - recalculate scale (used in budget tab - temporarily disabled)
  const _handleDragEnd = useCallback(() => {
    _setIsDragging(false);
  }, []);


  // Initialize business name from business data
  useEffect(() => {
    if (business?.brand_name) {
      setBusinessName(business.brand_name);
      setSavedBusinessName(business.brand_name);
    }
  }, [business?.brand_name]);

  // Initialize business description from business data
  useEffect(() => {
    const desc = formatBusinessDescription(business?.business_desc || '');
    setBusinessDesc(desc);
    setSavedBusinessDesc(desc);
  }, [business?.business_desc]);

  // Initialize business URL from business data
  useEffect(() => {
    const url = business?.business_url || '';
    setBusinessUrl(url);
    setSavedBusinessUrl(url);
  }, [business?.business_url]);

  // Initialize goals + biggest challenge from business data
  useEffect(() => {
    const goals = business?.goals || '';
    setBusinessGoals(goals);
    setSavedBusinessGoals(goals);
  }, [business?.goals]);

  useEffect(() => {
    const challenge = business?.biggest_challenge || '';
    setBiggestChallenge(challenge);
    setSavedBiggestChallenge(challenge);
  }, [business?.biggest_challenge]);

  // Initialize LLM instructions from business settings
  useEffect(() => {
    const instructions = businessSettings?.llm_instructions || '';
    setLlmInstructions(instructions);
    setSavedLlmInstructions(instructions);
  }, [businessSettings?.llm_instructions]);

  // Initialize editable products from derived products
  useEffect(() => {
    setEditableProducts(products);
    setSavedProducts(products);
  }, [products]);

  // Initialize editable personas from derived personas
  useEffect(() => {
    setEditablePersonas(personas);
    setSavedPersonas(personas);
  }, [personas]);

  // Initialize editable product-ICP links
  useEffect(() => {
    const links = business?.product_icp_links || [];
    setEditableProductIcpLinks(links);
    setSavedProductIcpLinks(links);
  }, [business?.product_icp_links]);

  // Initialize editable differentiators from brand identity
  useEffect(() => {
    const differentiators = brandIdentity.differentiators || [];
    setEditableDifferentiators(differentiators);
    setSavedDifferentiators(differentiators);
  }, [brandIdentity.differentiators]);

  // Initialize editable voice from brand identity
  useEffect(() => {
    const voice = brandIdentity.voice;
    setEditableVoice(voice);
    setSavedVoice(voice);
  }, [brandIdentity.voice]);

  // Initialize editable archetypes from brand identity
  useEffect(() => {
    const archetypes = brandIdentity.archetypes;
    setEditableArchetypes(archetypes);
    setSavedArchetypes(archetypes);
  }, [brandIdentity.archetypes]);


  // Initialize settings from business data
  useEffect(() => {
    if (businessSettings) {
      setSellsServices(businessSettings.sells_services ?? false);
      setDiscoverMode(businessSettings.brand_adherence_mode === 'discovery');
    }
  }, [businessSettings]);

  // Initialize trust signals from business data
  useEffect(() => {
    const signals = getTrustSignalsFromBusiness(business ?? null);
    setTrustSignals(signals);
    setSavedTrustSignals(signals);
  }, [business]);

  // Initialize industry from business data
  useEffect(() => {
    const industry = getIndustryFromBusiness(business ?? null);
    if (industry) {
      setIndustryValue(industry);
      setSavedIndustryValue(industry);
    }
  }, [business]);

  // Initialize platform budgets from business data (used in budget tab - temporarily disabled)
  useEffect(() => {
    const budgets = getPlatformBudgetsFromBusiness(business ?? null);
    setPlatformBudgets(budgets);
    setSavedPlatformBudgets(budgets);
    _setTotalBudget(budgets.google + budgets.meta + budgets.linkedin);
  }, [business]);

  // Calculate total from platform budgets (used in budget tab - temporarily disabled)
  const _calculateTotal = useCallback(() => {
    return platformBudgets.google + platformBudgets.meta + platformBudgets.linkedin;
  }, [platformBudgets]);

  // Update total when platform budgets change (if not editing total directly) (used in budget tab - temporarily disabled)
  useEffect(() => {
    if (!_isEditingTotal) {
      _setTotalBudget(_calculateTotal());
    }
  }, [platformBudgets, _calculateTotal, _isEditingTotal]);

  // Handle platform budget change (used in budget tab - temporarily disabled)
  const _handlePlatformBudgetChange = (platform: 'google' | 'meta' | 'linkedin', value: number) => {
    _setIsEditingTotal(false);
    setPlatformBudgets(prev => ({
      ...prev,
      [platform]: value,
    }));
  };

  // Track if platform budgets have unsaved changes (used in budget tab - temporarily disabled)
  const _isPlatformDirty = useMemo(() => {
    return platformBudgets.google !== savedPlatformBudgets.google ||
           platformBudgets.meta !== savedPlatformBudgets.meta ||
           platformBudgets.linkedin !== savedPlatformBudgets.linkedin;
  }, [platformBudgets, savedPlatformBudgets]);

  // Reset platform animation state when no longer dirty (used in budget tab - temporarily disabled)
  useEffect(() => {
    if (!_isPlatformDirty) {
      _setHasPlatformAnimated(false);
    }
  }, [_isPlatformDirty]);

  // Mark platform as animated after first render when dirty (used in budget tab - temporarily disabled)
  useEffect(() => {
    if (_isPlatformDirty && !_hasPlatformAnimated) {
      const timer = setTimeout(() => _setHasPlatformAnimated(true), 500);
      return () => clearTimeout(timer);
    }
  }, [_isPlatformDirty, _hasPlatformAnimated]);

  // Track if trust signals have unsaved changes
  const isTrustSignalsDirty = useMemo(() => {
    if (trustSignals.length !== savedTrustSignals.length) return true;
    return trustSignals.some((signal, idx) =>
      signal.id !== savedTrustSignals[idx]?.id ||
      signal.name !== savedTrustSignals[idx]?.name
    );
  }, [trustSignals, savedTrustSignals]);

  // Business description state
  const [businessDesc, setBusinessDesc] = useState('');
  const [savedBusinessDesc, setSavedBusinessDesc] = useState('');

  // Business URL state
  const [businessUrl, setBusinessUrl] = useState('');
  const [savedBusinessUrl, setSavedBusinessUrl] = useState('');

  // Goals + biggest challenge state — free-text, captured during JIP kickoff
  const [businessGoals, setBusinessGoals] = useState('');
  const [savedBusinessGoals, setSavedBusinessGoals] = useState('');
  const [biggestChallenge, setBiggestChallenge] = useState('');
  const [savedBiggestChallenge, setSavedBiggestChallenge] = useState('');

  // LLM Instructions state
  const [llmInstructions, setLlmInstructions] = useState('');
  const [savedLlmInstructions, setSavedLlmInstructions] = useState('');

  // Products state (editable local copy)
  const [editableProducts, setEditableProducts] = useState<Product[]>([]);
  const [savedProducts, setSavedProducts] = useState<Product[]>([]);

  // ICPs/Personas state (editable local copy)
  const [editablePersonas, setEditablePersonas] = useState<Persona[]>([]);
  const [savedPersonas, setSavedPersonas] = useState<Persona[]>([]);

  // Product-ICP links state (editable local copy)
  const [editableProductIcpLinks, setEditableProductIcpLinks] = useState<{ product_id: string; icp_id: string }[]>([]);
  const [savedProductIcpLinks, setSavedProductIcpLinks] = useState<{ product_id: string; icp_id: string }[]>([]);

  // Differentiators state (editable local copy)
  const [editableDifferentiators, setEditableDifferentiators] = useState<string[]>([]);
  const [savedDifferentiators, setSavedDifferentiators] = useState<string[]>([]);

  // Voice state (editable local copy)
  const [editableVoice, setEditableVoice] = useState<{ formality: number; humor: number; respect: number; enthusiasm: number; technicality: number }>({
    formality: 3, humor: 3, respect: 3, enthusiasm: 3, technicality: 3
  });
  const [savedVoice, setSavedVoice] = useState<{ formality: number; humor: number; respect: number; enthusiasm: number; technicality: number }>({
    formality: 3, humor: 3, respect: 3, enthusiasm: 3, technicality: 3
  });

  // Archetypes state (editable local copy)
  const [editableArchetypes, setEditableArchetypes] = useState<{ primary: string; secondary: string | null }>({
    primary: 'Sage', secondary: null
  });
  const [savedArchetypes, setSavedArchetypes] = useState<{ primary: string; secondary: string | null }>({
    primary: 'Sage', secondary: null
  });

  // Archetype selection modal state
  const [isArchetypeModalOpen, setIsArchetypeModalOpen] = useState(false);
  const [modalPrimary, setModalPrimary] = useState<string | null>(null);
  const [modalSecondary, setModalSecondary] = useState<string | null>(null);
  const [selectedArchetypeId, setSelectedArchetypeId] = useState<string | null>(null);

  // Keep refs in sync so useEffects below always read current selection
  const selectedPersonaIdRef = useRef(selectedPersonaId);
  useEffect(() => { selectedPersonaIdRef.current = selectedPersonaId; });
  const selectedProductIdRef = useRef(selectedProductId);
  useEffect(() => { selectedProductIdRef.current = selectedProductId; });

  // Reset selected product/persona when organization changes
  // This ensures we don't show stale selections from the previous organization
  const prevOrgId = useRef(organizationData?.id);
  useEffect(() => {
    if (organizationData?.id !== prevOrgId.current) {
      setSelectedPersonaId(null);
      setSelectedProductId(null);
      prevOrgId.current = organizationData?.id;
    }
  }, [organizationData?.id]);

  // Initialize selected persona/product when server data loads
  // Select first item if: no selection, or selection doesn't exist in server data (stale)
  // BUT don't reset if selection is a temp ID (newly added item not yet saved)
  useEffect(() => {
    if (personas.length > 0) {
      const current = selectedPersonaIdRef.current;
      const isTemp = current?.startsWith('persona-');
      const existsInData = personas.some(p => p.id === current);
      if (!current || (!isTemp && !existsInData)) {
        setSelectedPersonaId(personas[0].id);
      }
    }
  }, [personas]);

  useEffect(() => {
    if (products.length > 0) {
      const current = selectedProductIdRef.current;
      const isTemp = current?.startsWith('prod-');
      const existsInData = products.some(p => p.id === current);
      if (!current || (!isTemp && !existsInData)) {
        setSelectedProductId(products[0].id);
      }
    }
  }, [products]);

  // Track if business name has unsaved changes
  const isBusinessNameDirty = useMemo(() => {
    return businessName !== savedBusinessName;
  }, [businessName, savedBusinessName]);

  // Track if business description has unsaved changes
  const isBusinessDescDirty = useMemo(() => {
    return businessDesc !== savedBusinessDesc;
  }, [businessDesc, savedBusinessDesc]);

  // Track if business URL has unsaved changes
  const isBusinessUrlDirty = useMemo(() => {
    return businessUrl !== savedBusinessUrl;
  }, [businessUrl, savedBusinessUrl]);

  const isBusinessGoalsDirty = useMemo(() => {
    return businessGoals !== savedBusinessGoals;
  }, [businessGoals, savedBusinessGoals]);

  const isBiggestChallengeDirty = useMemo(() => {
    return biggestChallenge !== savedBiggestChallenge;
  }, [biggestChallenge, savedBiggestChallenge]);

  // Track if industry has unsaved changes
  const isIndustryDirty = useMemo(() => {
    return industryValue !== savedIndustryValue;
  }, [industryValue, savedIndustryValue]);

  // Track if LLM instructions have unsaved changes
  const isLlmInstructionsDirty = useMemo(() => {
    return llmInstructions !== savedLlmInstructions;
  }, [llmInstructions, savedLlmInstructions]);

  // Track if settings toggles have unsaved changes
  const isSettingsDirty = useMemo(() => {
    const currentSellsServices = businessSettings?.sells_services ?? false;
    const currentBrandAdherence = businessSettings?.brand_adherence_mode ?? 'strict';
    return sellsServices !== currentSellsServices ||
           (discoverMode ? 'discovery' : 'strict') !== currentBrandAdherence ||
           isLlmInstructionsDirty;
  }, [sellsServices, discoverMode, businessSettings, isLlmInstructionsDirty]);

  // Track if products have unsaved changes
  const isProductsDirty = useMemo(() => {
    if (editableProducts.length !== savedProducts.length) return true;
    return editableProducts.some((prod, idx) => {
      const saved = savedProducts[idx];
      if (!saved) return true;
      return prod.name !== saved.name ||
             prod.description !== saved.description ||
             JSON.stringify(prod.marketTypes) !== JSON.stringify(saved.marketTypes) ||
             prod.landingPageUrl !== saved.landingPageUrl;
    });
  }, [editableProducts, savedProducts]);

  // Track if personas/ICPs have unsaved changes
  const isPersonasDirty = useMemo(() => {
    if (editablePersonas.length !== savedPersonas.length) return true;
    return editablePersonas.some((persona, idx) => {
      const saved = savedPersonas[idx];
      if (!saved) return true;
      return persona.name !== saved.name ||
             JSON.stringify(persona.painPoints) !== JSON.stringify(saved.painPoints) ||
             JSON.stringify(persona.goals) !== JSON.stringify(saved.goals);
    });
  }, [editablePersonas, savedPersonas]);

  // Track if product-ICP links have unsaved changes
  const isProductIcpLinksDirty = useMemo(() => {
    if (editableProductIcpLinks.length !== savedProductIcpLinks.length) return true;
    return editableProductIcpLinks.some((link, idx) => {
      const saved = savedProductIcpLinks[idx];
      if (!saved) return true;
      return link.product_id !== saved.product_id || link.icp_id !== saved.icp_id;
    });
  }, [editableProductIcpLinks, savedProductIcpLinks]);

  // Track if differentiators have unsaved changes
  const isDifferentiatorsDirty = useMemo(() => {
    if (editableDifferentiators.length !== savedDifferentiators.length) return true;
    return editableDifferentiators.some((diff, idx) => diff !== savedDifferentiators[idx]);
  }, [editableDifferentiators, savedDifferentiators]);

  // Track if voice has unsaved changes
  const isVoiceDirty = useMemo(() => {
    return editableVoice.formality !== savedVoice.formality ||
           editableVoice.humor !== savedVoice.humor ||
           editableVoice.respect !== savedVoice.respect ||
           editableVoice.enthusiasm !== savedVoice.enthusiasm ||
           editableVoice.technicality !== savedVoice.technicality;
  }, [editableVoice, savedVoice]);

  // Track if archetypes have unsaved changes
  const isArchetypesDirty = useMemo(() => {
    return editableArchetypes.primary !== savedArchetypes.primary ||
           editableArchetypes.secondary !== savedArchetypes.secondary;
  }, [editableArchetypes, savedArchetypes]);

  // Handler to toggle differentiator selection (max 5)
  const handleDifferentiatorToggle = useCallback((option: string) => {
    setEditableDifferentiators(prev => {
      if (prev.includes(option)) {
        // Remove if already selected
        return prev.filter(d => d !== option);
      } else if (prev.length < 5) {
        // Add if under limit
        return [...prev, option];
      }
      // Already at max, don't add
      return prev;
    });
  }, []);

  // Handler to update voice dimension
  const handleVoiceChange = useCallback((dimension: keyof typeof editableVoice, value: number) => {
    setEditableVoice(prev => ({ ...prev, [dimension]: value }));
  }, []);

  // Open archetype modal with current values
  const openArchetypeModal = useCallback((preselectedId?: string) => {
    setModalPrimary(editableArchetypes.primary);
    setModalSecondary(editableArchetypes.secondary);
    setSelectedArchetypeId(preselectedId || null);
    setIsArchetypeModalOpen(true);
  }, [editableArchetypes]);

  // Handle archetype card click in modal - just select for viewing
  const handleArchetypeClick = useCallback((archetypeId: string) => {
    setSelectedArchetypeId(archetypeId);
  }, []);

  // Set selected archetype as primary
  const handleSetAsPrimary = useCallback(() => {
    if (selectedArchetypeId) {
      // If it was secondary, remove from secondary
      if (modalSecondary === selectedArchetypeId) {
        setModalSecondary(null);
      }
      setModalPrimary(selectedArchetypeId);
      setEditableArchetypes(prev => ({
        primary: selectedArchetypeId,
        secondary: prev.secondary === selectedArchetypeId ? null : prev.secondary
      }));
      setIsArchetypeModalOpen(false);
    }
  }, [selectedArchetypeId, modalSecondary]);

  // Set selected archetype as secondary
  const handleSetAsSecondary = useCallback(() => {
    if (selectedArchetypeId && selectedArchetypeId !== modalPrimary) {
      setModalSecondary(selectedArchetypeId);
      setEditableArchetypes(prev => ({ ...prev, secondary: selectedArchetypeId }));
      setIsArchetypeModalOpen(false);
    }
  }, [selectedArchetypeId, modalPrimary]);

  // Track whether any field is dirty for Save button visibility
  const isAnythingDirty = isBudgetV2Dirty || isTrustSignalsDirty || isBusinessNameDirty || isBusinessDescDirty || isBusinessUrlDirty || isIndustryDirty || isSettingsDirty || isProductsDirty || isPersonasDirty || isProductIcpLinksDirty || isDifferentiatorsDirty || isVoiceDirty || isArchetypesDirty || isBusinessGoalsDirty || isBiggestChallengeDirty || isKpiTargetsDirty || isLocationsDirty;
  // Fields hydrate their draft/original state across the first render(s) as values arrive from
  // queries/atoms, which can momentarily read as "dirty" and flash the blue Save button in and out
  // as the page mounts (#4735). Don't surface the button until a clean baseline is observed after
  // mount; a short fallback still arms it if the page genuinely loads with unsaved changes.
  const [baselineReady, setBaselineReady] = useState(false);
  useEffect(() => {
    if (baselineReady) return;
    if (!isAnythingDirty && !isSaving) {
      setBaselineReady(true);
      return;
    }
    const timer = setTimeout(() => setBaselineReady(true), 400);
    return () => clearTimeout(timer);
  }, [baselineReady, isAnythingDirty, isSaving]);

  const showSaveButton = baselineReady && (isAnythingDirty || isSaving);

  // Unified save handler for all business fields
  const handleSaveAllChanges = async () => {
    if (!business?.id) return;

    const productsWithoutLandingPage = editableProducts.filter((p) => !p.landingPageUrl);
    if (productsWithoutLandingPage.length > 0) {
      productsWithoutLandingPage.forEach((p) => {
        toast({
          title: `${p.name}: no landing page set`,
          description: 'Each product must have a landing page URL before saving.',
          variant: 'destructive',
        });
      });
      return;
    }

    // Set isSaving at the top of the flow (not inside the inner if) so the
    // button shows a spinner across the entire save — including budget-only
    // saves (where updateData is empty) and the gap between the business
    // mutation and the optional budget save. #4387.
    setIsSaving(true);
    try {
      const updateData: Record<string, unknown> = {};

      // Collect all dirty fields
      if (isBusinessNameDirty) {
        updateData.brand_name = businessName;
      }

      if (isBusinessDescDirty) {
        updateData.business_desc = businessDesc;
      }

      if (isBusinessUrlDirty) {
        updateData.business_url = businessUrl;
      }

      if (isTrustSignalsDirty) {
        updateData.trust_signals = trustSignals.map(ts => ts.name);
      }

      if (isBusinessGoalsDirty) {
        updateData.goals = businessGoals;
      }

      if (isBiggestChallengeDirty) {
        updateData.biggest_challenge = biggestChallenge;
      }

      if (isIndustryDirty) {
        // Find the industry from INDUSTRIES to get the full object with value and tier
        const selectedIndustry = INDUSTRIES.find(ind => ind.label === industryValue);
        if (selectedIndustry) {
          // Convert numeric tier to string tier expected by backend
          const tierMap: Record<number, 'highly_regulated' | 'restricted' | 'standard'> = {
            1: 'highly_regulated',
            2: 'restricted',
            3: 'standard',
          };
          updateData.industry = {
            value: selectedIndustry.value,
            label: selectedIndustry.label,
            tier: tierMap[selectedIndustry.tier] || 'standard',
          };
        } else if (industryValue) {
          // Custom industry entered - treat as standard tier
          updateData.industry = {
            value: industryValue.toLowerCase().replace(/\s+/g, '-'),
            label: industryValue,
            tier: 'standard',
          };
        }
      }

      // Settings changes (sells_services, brand_adherence_mode, llm_instructions)
      if (isSettingsDirty) {
        const newSettings = {
          ...businessSettings,
          sells_services: sellsServices,
          brand_adherence_mode: discoverMode ? 'discovery' : 'strict',
          llm_instructions: llmInstructions,
        };
        updateData.settings = newSettings;
      }

      // Products changes
      if (isProductsDirty) {
        // Convert UI Product format back to backend ProductData format
        updateData.products_data = editableProducts.map(prod => ({
          id: prod.id,
          name: prod.name,
          description: prod.description,
          market_types: prod.marketTypes,
          features: prod.features.filter(f => f.trim() !== ''),
          landing_page_url: prod.landingPageUrl || null,
        }));
      }

      // ICPs/Personas changes
      if (isPersonasDirty) {
        // Convert UI Persona format back to backend Icp format
        updateData.icps = editablePersonas.map(persona => ({
          id: persona.id,
          name: persona.name,
          title: persona.jobTitle || '',
          market_types: [persona.type],
          firmographics: {
            industries: persona.industries || [],
            company_size: persona.companySize || '',
            revenue: persona.revenue || '',
          },
          demographics: {
            age_range: persona.ageRange || '',
            gender: persona.gender || '',
            income_range: persona.incomeRange || '',
            location: '',
          },
          pgob: {
            pains: persona.painPoints,
            goals: persona.goals,
            objections: persona.objections,
            buying_triggers: persona.buyingTriggers,
          },
        }));
      }

      // Product-ICP links changes
      if (isProductIcpLinksDirty) {
        updateData.product_icp_links = editableProductIcpLinks;
      }

      // Brand identity changes (voice, differentiators, and/or archetypes)
      if (isDifferentiatorsDirty || isVoiceDirty || isArchetypesDirty) {
        updateData.brand_identity = {
          ...brandIdentity,
          ...(isDifferentiatorsDirty && { differentiators: editableDifferentiators }),
          ...(isVoiceDirty && { voice: editableVoice }),
          ...(isArchetypesDirty && { archetypes: editableArchetypes }),
        };
      }

      // One atomic request for the business profile: core fields + KPI targets + locations succeed
      // or roll back together (#5401). The KPI/locations sections hand up their payload here and
      // adopt the server's saved baseline afterwards. Budget stays on its own endpoints below.
      const businessData = Object.keys(updateData).length > 0 ? updateData : undefined;
      const kpiPayload = isKpiTargetsDirty ? (kpiTargetsControlRef.current?.getPayload() ?? undefined) : undefined;
      const locationsPayload = isLocationsDirty ? (locationsControlRef.current?.getPayload() ?? undefined) : undefined;

      if (businessData || kpiPayload || locationsPayload) {
        const result = await saveBusinessMutation.mutateAsync({
          businessId: business.id,
          payload: {
            // Core-field saved state resets via the useEffect hooks when query invalidation refetches.
            business: businessData,
            kpi_targets: kpiPayload,
            locations: locationsPayload,
          },
        });
        if (kpiPayload && result.kpi_targets) kpiTargetsControlRef.current?.markSaved(result.kpi_targets);
        if (locationsPayload && result.locations) locationsControlRef.current?.markSaved(result.locations);
      }

      // Budget saves on its own endpoints — a live-campaign budget syncs to Google Ads, which can't
      // join a DB transaction, so it keeps its per-campaign Google-reject rollback (#5401).
      if (isBudgetV2Dirty && budgetSaveRef.current) {
        await budgetSaveRef.current();
      }

      setSaveSuccess(true);
      window.setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('[Business Save] Failed to save changes:', error);
      // The atomic save rolls everything back on failure (e.g. a location still targeted by a
      // campaign → 409), so surface why nothing saved instead of failing silently (#5401).
      toast({
        title: 'Could not save changes',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // True while the brand profile is still generating — the content area shows the loading
  // carousel, so the per-tab Add buttons must stay hidden until generation finishes.
  const isGeneratingProfile = isGeneratingBrandIntel || (isTaskTrackingLoading && !business?.brand_identity);

  // Warn (and block) when navigating away with unsaved edits. Discard is implicit — the page's
  // edits live in component-local state, so leaving unmounts the page and drops them, so no
  // onDiscard is needed (unlike campaigns, whose draft persists in a Jotai atom). #5484.
  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty: isAnythingDirty,
    onSave: handleSaveAllChanges,
    isSaving,
    allowedBasePath: '/business',
    contextName: businessName || undefined,
  });

  return (
    <div className="flex flex-col h-full min-w-0">
      {/* PDF Export Loading Modal */}
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 w-80">
            <CardContent className="p-0">
              <div className="text-center">
                <Spinner size="xl" className="mx-auto mb-4 text-[#290BD9]" />
                <p className="text-gray-900 font-medium mb-2">{stage || 'Preparing export...'}</p>
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-gray-500 mt-2">{Math.round(progress)}% complete</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3">
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-6">
      {/* Show empty state if: */}
      {/* 1. Businesses are still loading - show nothing (prevents flash of empty state) */}
      {/* 2. No business exists AND done loading, OR */}
      {/* 3. Business exists but onboarding never completed (no brand + no products + no customers) AND not currently generating */}
      {isBusinessesLoading ? null : isBusinessesError ? (
        <PageErrorState
          title="Couldn't Load Your Business"
          description="Your business profile failed to load. This is a loading error, not a missing profile, so no data has been lost. Retry, or contact support if it persists."
          onRetry={() => refetchBusiness()}
        />
      ) : !business || (!business.brand_identity && !business.products_data?.length && !business.icps?.length && !isGeneratingBrandIntel && !isTaskTrackingLoading && !isTaskTrackingError && !hasBrandIntelFailed) ? (
        <PageEmptyState
          icon={<Rocket className="h-12 w-12 text-gray-500" strokeWidth={1} />}
          title="Set Up Your Business"
          description="Your business profile controls how ads are built and optimized"
          action={
            <Button
              onClick={() => navigate({ to: '/new/brand' })}
              size="lg"
              className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
            >
              <span>CREATE BUSINESS</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          }
        />
      ) : hasBrandIntelFailed || (isTaskTrackingError && !hasBusinessData) ? (
        <PageEmptyState
          icon={<AlertCircle className="h-12 w-12 text-[#B91C1C]" strokeWidth={1} />}
          title="Business Setup Failed"
          description={
            taskTracking?.latest_error_message
              || "An error was encountered while generating your business profile. This may be due to website bot protection (e.g. Cloudflare). You can: (1) retry, (2) try a different URL for your website, or (3) contact support for assistance."
          }
          action={
            <div className="flex flex-col items-center gap-3">
              <Button
                onClick={() => {
                  if (business) {
                    generateBrandIntelMutation.mutate(business.id);
                  }
                }}
                disabled={generateBrandIntelMutation.isPending}
                size="lg"
                className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
              >
                {generateBrandIntelMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <span>RETRY</span>
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setManualBrandEntry(true);
                  navigate({ to: '/new/brand' });
                }}
                variant="outline"
                size="lg"
                className="w-[280px] py-6 flex items-center justify-center space-x-2"
              >
                <span>ENTER DETAILS MANUALLY</span>
              </Button>
            </div>
          }
        />
      ) : (
        <TabCardPanel
          tabs={tabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
            isActive: activeTab === tab.id,
            onClick: () => handleTabChange(tab.id),
            ...(tab.id === 'budget' && budgetNeedsAttention ? {
              render: () => (
                <button
                  onClick={() => handleTabChange('budget')}
                  className={`relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all ${activeTab === 'budget' ? 'bg-white text-gray-900 border border-gray-200 border-b-transparent' : 'bg-gray-100 text-gray-700 border border-transparent border-b-gray-200 hover:bg-gray-200 hover:text-gray-900'}`}
                >
                  {tab.label}
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">!</span>
                </button>
              ),
            } : {}),
          }))}
          actions={
            <>
              {/* One control owns save / saving / saved: it renders "Saved ✓" in place of itself,
                  never beside it (#5347). */}
              {(showSaveButton || saveSuccess) && (
                <SaveChangesButton
                  onClick={handleSaveAllChanges}
                  disabled={hasBudgetValidationError}
                  saving={isSaving}
                  saved={saveSuccess && !showSaveButton}
                  className="h-9 shadow-lg animate-bounce-in-right"
                />
              )}
              {activeTab === 'product' && !isGeneratingProfile && (
                <Button
                  variant="outline"
                  className="h-9 text-gray-900"
                  onClick={() => setProductModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add {sellsServices ? 'Service' : 'Product'}
                </Button>
              )}
              {activeTab === 'customers' && !isGeneratingProfile && (
                <Button
                  variant="outline"
                  className="h-9 text-gray-900"
                  onClick={() => setPersonaModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Persona
                </Button>
              )}
              <Button
                variant="outline"
                className="h-9"
                disabled={isExporting || isBusinessesLoading || isGeneratingBrandIntel}
                onClick={() => {
                  if (business) {
                    exportToPDF(business);
                  }
                }}
              >
                {isExporting ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </>
          }
        >
              {/* Brand Profile Generation Loading State */}
              {/* Also show loading while task tracking query is loading (to avoid flash to content then back to loading) */}
              {isGeneratingProfile ? (
                <BrandProfileLoadingCarousel />
              ) : (
              <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">Business Details</h2>
                  <div className="space-y-6">
                  {/* Identity (stacked) / Description (50/50) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                        <Input
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          className="bg-white"
                        />
                      </div>
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                        <div className="relative">
                          <Input
                            value={industryValue}
                            onChange={(e) => {
                              setIndustryValue(e.target.value);
                              setIndustryOpen(true);
                            }}
                            onFocus={() => setIndustryOpen(true)}
                            onBlur={() => setTimeout(() => setIndustryOpen(false), 150)}
                            placeholder="Type to search or enter custom..."
                            className="bg-white pr-10"
                          />
                          <ChevronsUpDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                        {industryOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                            {INDUSTRIES.filter((ind) =>
                              !industryValue || ind.label.toLowerCase().includes(industryValue.toLowerCase())
                            ).length > 0 ? (
                              INDUSTRIES.filter((ind) =>
                                !industryValue || ind.label.toLowerCase().includes(industryValue.toLowerCase())
                              ).map((industry) => (
                                <button
                                  key={industry.value}
                                  type="button"
                                  className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 flex items-center justify-between"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIndustryValue(industry.label);
                                    setIndustryOpen(false);
                                  }}
                                >
                                  {industry.label}
                                  {industry.tier < 3 && (
                                    <span className="text-xs text-gray-500">
                                      {industry.tier === 1 ? 'Highly Regulated' : 'Restrictions Apply'}
                                    </span>
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                Press Enter to use "{industryValue}"
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Main Website</label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={businessUrl}
                            onChange={(e) => setBusinessUrl(e.target.value)}
                            className="bg-white flex-1"
                            placeholder="https://example.com"
                          />
                          {businessUrl && (
                            <a
                              href={businessUrl.startsWith('http') ? businessUrl : `https://${businessUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-500 hover:text-[#290BD9] transition-colors"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Description</label>
                      <Textarea
                        value={businessDesc}
                        onChange={(e) => setBusinessDesc(e.target.value)}
                        rows={8}
                        className="bg-white"
                        placeholder="Describe what your business does..."
                      />
                    </div>
                  </div>

                  {/* Locations — full width (#4816) */}
                  {business && (
                    <BusinessLocations
                      business={business}
                      onDirtyChange={setIsLocationsDirty}
                      saveRef={locationsControlRef}
                    />
                  )}

                  {/* Trust Signals */}
                  <div className="grid grid-cols-1">
                    <BusinessTrustSignalView
                      signals={trustSignals}
                      setSignals={setTrustSignals}
                      title="Trust Signals"
                      description="Social proof, certifications, awards, and credibility indicators"
                    />
                  </div>
                  </div>
                </div>
              )}

              {/* Brand Tab */}
              {activeTab === 'brand' && (
                <div className="space-y-6">
                  {/* Two Column Layout - Personality/Voice on Left, Differentiators on Right */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left Column - Personality & Voice */}
                    <div className="lg:col-span-6 space-y-6">
                      {/* Personality */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Personality Archetype</h3>
                            <p className="text-sm text-gray-700 mt-1">The human traits your brand embodies. Pick a primary and secondary.</p>
                          </div>
                          <Button
                            variant="outline"
                            className="text-gray-900"
                            onClick={() => openArchetypeModal()}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          {/* Primary Detail Card */}
                          <div
                            className="p-6 rounded-xl text-center cursor-pointer transition-all hover:shadow-lg flex flex-col"
                            style={{ background: 'linear-gradient(215deg, #F7F7F7 35%, #D6CDEE 100%)' }}
                            onClick={() => openArchetypeModal(editableArchetypes.primary)}
                          >
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Primary Archetype</div>
                            <div className="text-5xl mb-3">{ARCHETYPES.find(a => a.id === editableArchetypes.primary)?.icon}</div>
                            <div className="text-xl font-bold text-gray-900 mb-1">The {editableArchetypes.primary}</div>
                            <div className="text-sm text-gray-600 mb-4">{ARCHETYPES.find(a => a.id === editableArchetypes.primary)?.fullTrait}</div>
                            <div className="text-sm text-gray-700 text-left flex-1">{ARCHETYPES.find(a => a.id === editableArchetypes.primary)?.desc}</div>
                            <div className="bg-white/50 rounded-lg p-3 text-left mt-4">
                              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Brand Examples</div>
                              <div className="text-sm text-gray-700">{ARCHETYPES.find(a => a.id === editableArchetypes.primary)?.examples}</div>
                            </div>
                          </div>

                          {/* Secondary Detail Card */}
                          <div
                            className={`p-6 rounded-xl text-center cursor-pointer transition-all hover:shadow-lg flex flex-col ${
                              editableArchetypes.secondary
                                ? ''
                                : 'border-2 border-dashed border-gray-200 bg-gray-50'
                            }`}
                            style={editableArchetypes.secondary ? { background: 'linear-gradient(215deg, #F7F7F7 35%, #CED6EF 100%)' } : {}}
                            onClick={() => openArchetypeModal(editableArchetypes.secondary || undefined)}
                          >
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Secondary Archetype</div>
                            {editableArchetypes.secondary ? (
                              <>
                                <div className="text-5xl mb-3">{ARCHETYPES.find(a => a.id === editableArchetypes.secondary)?.icon}</div>
                                <div className="text-xl font-bold text-gray-900 mb-1">The {editableArchetypes.secondary}</div>
                                <div className="text-sm text-gray-600 mb-4">{ARCHETYPES.find(a => a.id === editableArchetypes.secondary)?.fullTrait}</div>
                                <div className="text-sm text-gray-700 text-left flex-1">{ARCHETYPES.find(a => a.id === editableArchetypes.secondary)?.desc}</div>
                                <div className="bg-white/50 rounded-lg p-3 text-left mt-4">
                                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Brand Examples</div>
                                  <div className="text-sm text-gray-700">{ARCHETYPES.find(a => a.id === editableArchetypes.secondary)?.examples}</div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-5xl mb-3 opacity-30">?</div>
                                <div className="text-lg font-semibold text-gray-500">Optional</div>
                                <div className="text-sm text-gray-500">Click Edit to select</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Voice */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Voice</h3>
                        <p className="text-sm text-gray-700 mb-4">How your brand speaks</p>
                        <VoiceSlider
                          leftLabel="Casual"
                          rightLabel="Formal"
                          value={editableVoice.formality}
                          savedValue={savedVoice.formality}
                          dimension="formality"
                          onChange={(v) => handleVoiceChange('formality', v)}
                        />
                        <VoiceSlider
                          leftLabel="Serious"
                          rightLabel="Funny"
                          value={editableVoice.humor}
                          savedValue={savedVoice.humor}
                          dimension="humor"
                          onChange={(v) => handleVoiceChange('humor', v)}
                        />
                        <VoiceSlider
                          leftLabel="Irreverent"
                          rightLabel="Respectful"
                          value={editableVoice.respect}
                          savedValue={savedVoice.respect}
                          dimension="respect"
                          onChange={(v) => handleVoiceChange('respect', v)}
                        />
                        <VoiceSlider
                          leftLabel="Matter-of-fact"
                          rightLabel="Enthusiastic"
                          value={editableVoice.enthusiasm}
                          savedValue={savedVoice.enthusiasm}
                          dimension="enthusiasm"
                          onChange={(v) => handleVoiceChange('enthusiasm', v)}
                        />
                        <VoiceSlider
                          leftLabel="Simple"
                          rightLabel="Technical"
                          value={editableVoice.technicality}
                          savedValue={savedVoice.technicality}
                          dimension="technicality"
                          onChange={(v) => handleVoiceChange('technicality', v)}
                        />
                      </div>
                    </div>

                    {/* Right Column - Differentiators */}
                    <div className="lg:col-span-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Differentiators</h3>
                      <p className="text-sm text-gray-700 mb-4">
                        Your brand's defining strengths ({editableDifferentiators.length}/5 selected)
                      </p>
                      <div className="space-y-5">
                        {DIFFERENTIATOR_CATEGORIES.map((category) => (
                          <div key={category.name}>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              {category.name}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {category.options.map((option) => {
                                const isSelected = editableDifferentiators.includes(option);
                                const isDisabled = !isSelected && editableDifferentiators.length >= 5;
                                return (
                                  <DifferentiatorChip
                                    key={option}
                                    label={option}
                                    isSelected={isSelected}
                                    onClick={isDisabled ? undefined : () => handleDifferentiatorToggle(option)}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* Product Tab */}
              {activeTab === 'product' && (
                <div className="grid grid-cols-12 gap-0 -m-6">
                  {/* Left Column - Products List (4 cols) */}
                  <div className="col-span-4 border-r border-gray-200">
                    {editableProducts.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {editableProducts.map((product) => {
                          const linkedPersonas = getLinkedPersonas(product, editablePersonas);
                          return (
                            <div
                              key={product.id}
                              onClick={() => setSelectedProductId(product.id)}
                              className={`p-4 cursor-pointer transition-colors ${
                                selectedProductId === product.id
                                  ? 'bg-[#290BD9]/10'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8">
                                  <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                                    {sellsServices ? (
                                      <Briefcase className="h-4 w-4 text-[#290BD9]" />
                                    ) : (
                                      <Package className="h-4 w-4 text-[#290BD9]" />
                                    )}
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    {product.marketTypes.map(type => (
                                      <span
                                        key={type}
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                          type === 'B2B'
                                            ? 'bg-blue-100 text-blue-800'
                                            : type === 'B2C'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-purple-100 text-purple-800'
                                        }`}
                                      >
                                        {type}
                                      </span>
                                    ))}
                                    <span className="text-sm text-gray-500">
                                      · {linkedPersonas.length} ideal customer{linkedPersonas.length !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-6">
                        <EmptyState
                          icon={<Package className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                          title={`No ${sellsServices ? 'Services' : 'Products'}`}
                          description={`${sellsServices ? 'Services' : 'Products'} will appear here once added`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Right Column - Product Detail (8 cols) */}
                  <div className="col-span-8 p-6">
                    {editableProducts.find(p => p.id === selectedProductId) && (
                      <ProductDetailEditable
                        product={editableProducts.find(p => p.id === selectedProductId)!}
                        allPersonas={editablePersonas}
                        onUpdate={(updatedProduct) => {
                          setEditableProducts(prev =>
                            prev.map(p => p.id === updatedProduct.id ? updatedProduct : p)
                          );
                        }}
                        onDelete={(productId) => {
                          const product = editableProducts.find(p => p.id === productId);
                          if (product) setProductToDelete(product);
                        }}
                        onLinkPersona={(productId, personaId) => {
                          setEditableProductIcpLinks(prev => [...prev, { product_id: productId, icp_id: personaId }]);
                          // Also update the product's linkedPersonaIds
                          setEditableProducts(prev =>
                            prev.map(p => p.id === productId
                              ? { ...p, linkedPersonaIds: [...p.linkedPersonaIds, personaId] }
                              : p
                            )
                          );
                        }}
                        onUnlinkPersona={(productId, personaId) => {
                          setEditableProductIcpLinks(prev =>
                            prev.filter(link => !(link.product_id === productId && link.icp_id === personaId))
                          );
                          // Also update the product's linkedPersonaIds
                          setEditableProducts(prev =>
                            prev.map(p => p.id === productId
                              ? { ...p, linkedPersonaIds: p.linkedPersonaIds.filter(id => id !== personaId) }
                              : p
                            )
                          );
                        }}
                        onNavigateToPersona={(personaId) => {
                          setSelectedPersonaId(personaId);
                          handleTabChange('customers');
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Audience Tab */}
              {activeTab === 'customers' && (
                <div className="grid grid-cols-12 gap-0 -m-6">
                  {/* Left Column - Personas List (4 cols) */}
                  <div className="col-span-4 border-r border-gray-200">
                    {editablePersonas.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {editablePersonas.map((persona) => {
                          const linkedProducts = getLinkedProducts(persona, editableProducts);
                          return (
                            <div
                              key={persona.id}
                              onClick={() => setSelectedPersonaId(persona.id)}
                              className={`p-4 cursor-pointer transition-colors ${
                                selectedPersonaId === persona.id
                                  ? 'bg-[#290BD9]/10'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8">
                                  <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                                    <User className="h-4 w-4 text-[#290BD9]" />
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{persona.name}</div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        persona.type === 'B2B'
                                          ? 'bg-blue-100 text-blue-800'
                                          : 'bg-green-100 text-green-800'
                                      }`}
                                    >
                                      {persona.type}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      · {linkedProducts.length} product{linkedProducts.length !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-6">
                        <EmptyState
                          icon={<Users className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                          title="No Customers"
                          description="Customer personas represent the people most likely to buy your product"
                        />
                      </div>
                    )}
                  </div>

                  {/* Right Column - Persona Detail (8 cols) */}
                  <div className="col-span-8 p-6">
                    {editablePersonas.find(p => p.id === selectedPersonaId) && (
                      <PersonaDetailEditable
                        persona={editablePersonas.find(p => p.id === selectedPersonaId)!}
                        allProducts={editableProducts}
                        onUpdate={(updatedPersona) => {
                          setEditablePersonas(prev =>
                            prev.map(p => p.id === updatedPersona.id ? updatedPersona : p)
                          );
                        }}
                        onDelete={(personaId) => {
                          const persona = editablePersonas.find(p => p.id === personaId);
                          if (persona) setPersonaToDelete(persona);
                        }}
                        onLinkProduct={(personaId, productId) => {
                          setEditableProductIcpLinks(prev => [...prev, { product_id: productId, icp_id: personaId }]);
                          // Also update the persona's linkedProductIds
                          setEditablePersonas(prev =>
                            prev.map(p => p.id === personaId
                              ? { ...p, linkedProductIds: [...p.linkedProductIds, productId] }
                              : p
                            )
                          );
                          // And update the product's linkedPersonaIds
                          setEditableProducts(prev =>
                            prev.map(p => p.id === productId
                              ? { ...p, linkedPersonaIds: [...p.linkedPersonaIds, personaId] }
                              : p
                            )
                          );
                        }}
                        onUnlinkProduct={(personaId, productId) => {
                          setEditableProductIcpLinks(prev =>
                            prev.filter(link => !(link.product_id === productId && link.icp_id === personaId))
                          );
                          // Also update the persona's linkedProductIds
                          setEditablePersonas(prev =>
                            prev.map(p => p.id === personaId
                              ? { ...p, linkedProductIds: p.linkedProductIds.filter(id => id !== productId) }
                              : p
                            )
                          );
                          // And update the product's linkedPersonaIds
                          setEditableProducts(prev =>
                            prev.map(p => p.id === productId
                              ? { ...p, linkedPersonaIds: p.linkedPersonaIds.filter(id => id !== personaId) }
                              : p
                            )
                          );
                        }}
                        onNavigateToProduct={(productId) => {
                          setSelectedProductId(productId);
                          handleTabChange('product');
                        }}
                        sellsServices={sellsServices}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Legacy Budget Tab (hidden, kept for reference) */}
              {activeTab === 'budgetLegacy' && (
                <div className="grid grid-cols-12 gap-6">
                  {/* Platform Budgets - Left Column (3 cols) */}
                  <div className="col-span-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Platform Budgets</h3>
                    <p className="text-sm text-gray-700 mb-4">Monthly budget by platform</p>

                    {/* Platform list - vertical table-like layout */}
                    <div className="space-y-3">
                      {/* Google Ads */}
                      <div className="flex items-center gap-3">
                        <img src="/google-ads.svg" className="h-6 w-6 shrink-0" alt="Google Ads" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">Google Ads</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1.5 border rounded-md bg-white border-gray-300 focus-within:border-[#290BD9]">
                          <span className="text-sm text-gray-500">{_currencySymbol}</span>
                          <input
                            type="text"
                            value={platformBudgets.google.toLocaleString()}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                              _handlePlatformBudgetChange('google', value);
                            }}
                            onFocus={(e) => e.target.select()}
                            className="text-sm font-medium text-gray-900 bg-transparent border-none outline-none w-16 text-right focus:ring-0 p-0"
                          />
                        </div>
                      </div>

                      {/* Meta Ads */}
                      <div className="flex items-center gap-3">
                        <div className="h-6 w-6 rounded flex items-center justify-center bg-[#0081FB] shrink-0">
                          <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">Meta Ads</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1.5 border rounded-md bg-white border-gray-300 focus-within:border-[#290BD9]">
                          <span className="text-sm text-gray-500">{_currencySymbol}</span>
                          <input
                            type="text"
                            value={platformBudgets.meta.toLocaleString()}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                              _handlePlatformBudgetChange('meta', value);
                            }}
                            onFocus={(e) => e.target.select()}
                            className="text-sm font-medium text-gray-900 bg-transparent border-none outline-none w-16 text-right focus:ring-0 p-0"
                          />
                        </div>
                      </div>

                      {/* LinkedIn Ads */}
                      <div className="flex items-center gap-3">
                        <div className="h-6 w-6 rounded flex items-center justify-center bg-[#0A66C2] shrink-0">
                          <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">LinkedIn Ads</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1.5 border rounded-md bg-white border-gray-300 focus-within:border-[#290BD9]">
                          <span className="text-sm text-gray-500">{_currencySymbol}</span>
                          <input
                            type="text"
                            value={platformBudgets.linkedin.toLocaleString()}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                              _handlePlatformBudgetChange('linkedin', value);
                            }}
                            onFocus={(e) => e.target.select()}
                            className="text-sm font-medium text-gray-900 bg-transparent border-none outline-none w-16 text-right focus:ring-0 p-0"
                          />
                        </div>
                      </div>

                      {/* Total - read only */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <span className="text-sm font-semibold text-gray-900">Total (Monthly)</span>
                        <div className="flex items-center gap-1 px-2 py-1.5 border rounded-md bg-gray-50 border-gray-200">
                          <span className="text-sm text-gray-500">{_currencySymbol}</span>
                          <span className="text-sm font-medium text-gray-500 w-16 text-right">
                            {_totalBudget.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        ~{_currencySymbol}{Math.round(_totalBudget / 30).toLocaleString()}/day
                      </p>
                    </div>
                  </div>

                  {/* Per Campaign Allocation - Right Column (9 cols) */}
                  <div className="col-span-9">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Per Campaign Allocation</h3>
                    <p className="text-sm text-gray-700 mb-4">How your budget is distributed across campaigns</p>

                    {campaigns.length === 0 ? (
                      <EmptyState
                        icon={<Package className="h-12 w-12 text-gray-500" strokeWidth={1} />}
                        title="No Allocation Set"
                        description="Budget allocation will be configured when campaigns are created"
                      />
                    ) : (
                      <div className="border rounded-lg bg-white overflow-hidden">
                        {/* Table header */}
                        <div className="grid grid-cols-[180px_100px_1fr_120px_40px] gap-4 items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type</div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Allocation</div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Lock</div>
                        </div>

                        {/* Campaign list with sliders */}
                        <div className="px-4">
                          {campaigns.map(campaign => {
                            const allocation = campaignBudgets.find(cb => cb.campaignId === campaign.id) || {
                              campaignId: campaign.id,
                              budget: campaign.budget || 0,
                              isLocked: false,
                            };
                            return (
                              <_BudgetSliderRow
                                key={campaign.id}
                                campaign={campaign}
                                allocation={allocation}
                                sliderMax={_stableSliderMax}
                                currencySymbol={_currencySymbol}
                                onBudgetChange={_handleCampaignBudgetChange}
                                onLockToggle={_handleLockToggle}
                                onDragStart={_handleDragStart}
                                onDragEnd={_handleDragEnd}
                              />
                            );
                          })}
                        </div>

                        {/* Totals footer */}
                        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 rounded-b-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-900">Total Spend</span>
                            <div className="flex items-center gap-4">
                              <div className="flex items-baseline gap-0.5">
                                <span className="text-sm text-gray-500">{_currencySymbol}</span>
                                <span className="text-sm font-semibold text-gray-900">
                                  {_totalAllocatedBudget.toLocaleString()}
                                </span>
                                <span className="text-xs text-gray-500">/mo</span>
                              </div>
                              {platformBudgets.google > 0 && (
                                <span className={`text-xs ${_totalAllocatedBudget > platformBudgets.google ? 'text-red-600' : _totalAllocatedBudget < platformBudgets.google ? 'text-amber-600' : 'text-green-600'}`}>
                                  {_totalAllocatedBudget === platformBudgets.google
                                    ? 'Fully allocated'
                                    : _totalAllocatedBudget > platformBudgets.google
                                      ? `${_currencySymbol}${(_totalAllocatedBudget - platformBudgets.google).toLocaleString()} over budget`
                                      : `${_currencySymbol}${(platformBudgets.google - _totalAllocatedBudget).toLocaleString()} remaining`
                                  }
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Budget V2 Tab — kept mounted to preserve state */}
              <div className={activeTab === 'budget' ? '' : 'hidden'}>
                <BudgetV2 onDirtyChange={setIsBudgetV2Dirty} onValidationChange={setHasBudgetValidationError} onSaveRef={budgetSaveRef} />
              </div>

              {/* Goals Tab — KPI targets + free-text goals/challenge, captured during JIP kickoff */}
              {activeTab === 'goals' && (
                <div className="space-y-6">
                  <YourGoalKpiCard
                    onDirtyChange={setIsKpiTargetsDirty}
                    saveRef={kpiTargetsControlRef}
                  />

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Goals</h3>
                    <p className="text-sm text-gray-700 mb-2">Your high-level goals for the engagement.</p>
                    <Textarea
                      placeholder="What do you want to achieve?"
                      className="bg-white min-h-[200px]"
                      maxLength={1000}
                      value={businessGoals}
                      onChange={(e) => setBusinessGoals(e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{businessGoals.length}/1000 characters</p>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Biggest Challenge</h3>
                    <p className="text-sm text-gray-700 mb-2">The one thing you most want Glitch to solve.</p>
                    <Textarea
                      placeholder="What is your biggest challenge?"
                      className="bg-white min-h-[200px]"
                      maxLength={500}
                      value={biggestChallenge}
                      onChange={(e) => setBiggestChallenge(e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{biggestChallenge.length}/500 characters</p>
                  </div>
                  </div>
                </div>
              )}

              {/* Misc Tab */}
              {activeTab === 'context' && (
                <StrategyContextPanel orgId={organizationData?.id ?? 0} businessId={business.id} />
              )}

              {activeTab === 'misc' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Additional Instructions */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Additional AI Instructions</h3>
                      <p className="text-sm text-gray-700 mb-4">Any additional context for ad generation.</p>
                      <Textarea
                        className="bg-white min-h-[200px]"
                        maxLength={400}
                        placeholder={DEFAULT_AI_INSTRUCTIONS}
                        value={llmInstructions}
                        onChange={(e) => setLlmInstructions(e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1 text-right">{llmInstructions.length}/400 characters</p>
                    </div>
                  </div>

                  {/* Right Column - Options */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Configuration</h3>
                      <p className="text-sm text-gray-700 mb-4">Configure how your business is described</p>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-base font-medium text-gray-700">What you sell</span>
                          <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-500 w-16 text-right">Products</span>
                            <div
                              onClick={() => setSellsServices(!sellsServices)}
                              className={`relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer ${
                                sellsServices ? 'bg-[#290BD9]' : 'bg-[#290BD9]'
                              }`}
                            >
                              <div
                                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                  sellsServices ? 'right-0.5' : 'left-0.5'
                                }`}
                              />
                            </div>
                            <span className="text-sm text-gray-500 w-16">Services</span>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-base font-medium text-gray-700">AI Brand Adherence</span>
                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-gray-500 w-16 text-right">Strict</span>
                              <div
                                onClick={() => setDiscoverMode(!discoverMode)}
                                className={`relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer ${
                                  discoverMode ? 'bg-[#290BD9]' : 'bg-[#290BD9]'
                                }`}
                              >
                                <div
                                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                    discoverMode ? 'right-0.5' : 'left-0.5'
                                  }`}
                                />
                              </div>
                              <span className="text-sm text-gray-500 w-16">Discovery</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            In strict mode, the AI always generates content consistent with your brand values. In discovery mode, the AI tests a broader range of permutations to find higher performing ads but may not always align strictly to brand.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </>
              )}
        </TabCardPanel>
      )}

      {/* Archetype Selection Modal */}
      <Dialog open={isArchetypeModalOpen} onOpenChange={setIsArchetypeModalOpen}>
        <DialogContent className="max-w-4xl min-h-[570px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">Select Brand Archetype</DialogTitle>
            <p className="text-sm text-gray-700 mt-1">Choose an archetype to view details, then set as primary or secondary</p>
          </DialogHeader>

          <div className="py-4">
            {/* Compact Grid Container with Detail Panel */}
            <div className="flex gap-8">
              {/* 4x3 Grid */}
              <div className="flex-1 grid grid-cols-4 gap-3">
                {ARCHETYPES.map((archetype) => {
                  const isPrimary = modalPrimary === archetype.id;
                  const isSecondary = modalSecondary === archetype.id;
                  const isSelected = selectedArchetypeId === archetype.id;
                  return (
                    <div
                      key={archetype.id}
                      onClick={() => handleArchetypeClick(archetype.id)}
                      className={`p-3 rounded-lg border-2 text-center relative cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
                        isSelected && isSecondary
                          ? 'border-gray-400 shadow-md'
                          : isSelected
                          ? 'border-[#290BD9] shadow-md'
                          : isPrimary
                          ? 'border-[#290BD9]'
                          : isSecondary
                          ? 'border-gray-400'
                          : 'border-gray-200 hover:border-[#290BD9]'
                      }`}
                      style={{
                        background: isPrimary
                          ? 'linear-gradient(215deg, #F7F7F7 35%, #D6CDEE 100%)'
                          : isSecondary
                          ? 'linear-gradient(215deg, #F7F7F7 35%, #CED6EF 100%)'
                          : 'white',
                      }}
                    >
                      {/* Badge indicator */}
                      {isPrimary && (
                        <div className="absolute top-1 left-1 right-1">
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-[#290BD9]">Primary</span>
                        </div>
                      )}
                      {isSecondary && (
                        <div className="absolute top-1 left-1 right-1">
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-500">Secondary</span>
                        </div>
                      )}
                      <div className={`text-2xl mb-1 ${isPrimary || isSecondary ? 'mt-3' : ''}`}>{archetype.icon}</div>
                      <div className="text-xs font-semibold text-gray-900">{archetype.name}</div>
                    </div>
                  );
                })}
              </div>

              {/* Detail Panel - Single Card */}
              <div className="w-80 flex-shrink-0">
                {selectedArchetypeId ? (
                  <div
                    className="p-6 rounded-xl text-center"
                    style={{ background: 'linear-gradient(215deg, #F7F7F7 35%, #E8E4F0 100%)' }}
                  >
                    <div className="text-5xl mb-3">{ARCHETYPES.find(a => a.id === selectedArchetypeId)?.icon}</div>
                    <div className="text-xl font-bold text-gray-900 mb-1">The {selectedArchetypeId}</div>
                    <div className="text-sm text-gray-600 mb-4">{ARCHETYPES.find(a => a.id === selectedArchetypeId)?.fullTrait}</div>
                    <div className="text-sm text-gray-700 text-left mb-4">{ARCHETYPES.find(a => a.id === selectedArchetypeId)?.desc}</div>
                    <div className="bg-white/50 rounded-lg p-3 text-left mb-6">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Brand Examples</div>
                      <div className="text-sm text-gray-700">{ARCHETYPES.find(a => a.id === selectedArchetypeId)?.examples}</div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <Button
                        onClick={handleSetAsPrimary}
                        className="w-full bg-[#290BD9] hover:bg-[#2309C5] text-white"
                      >
                        Set as Primary
                      </Button>
                      <Button
                        onClick={handleSetAsSecondary}
                        disabled={selectedArchetypeId === modalPrimary}
                        variant="outline"
                        className="w-full"
                      >
                        Set as Secondary
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 rounded-xl text-center border-2 border-dashed border-gray-200 bg-gray-50">
                    <div className="text-5xl mb-3 opacity-30">?</div>
                    <div className="text-lg font-semibold text-gray-500">Select an Archetype</div>
                    <div className="text-sm text-gray-500">Click a card to view details</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Product Confirmation Modal */}
      <ConformationModal
        isOpen={productToDelete !== null}
        onClose={() => setProductToDelete(null)}
        onConfirm={() => {
          if (productToDelete) {
            setEditableProducts(prev => prev.filter(p => p.id !== productToDelete.id));
            const remaining = editableProducts.filter(p => p.id !== productToDelete.id);
            setSelectedProductId(remaining[0]?.id || null);
            setProductToDelete(null);
          }
        }}
        title={`Remove ${sellsServices ? 'Service' : 'Product'}`}
        description={`Are you sure you want to remove "${productToDelete?.name || ''}"? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />

      {/* Delete Customer Profile Confirmation Modal */}
      <ConformationModal
        isOpen={personaToDelete !== null}
        onClose={() => setPersonaToDelete(null)}
        onConfirm={() => {
          if (personaToDelete) {
            setEditablePersonas(prev => prev.filter(p => p.id !== personaToDelete.id));
            const remaining = editablePersonas.filter(p => p.id !== personaToDelete.id);
            setSelectedPersonaId(remaining[0]?.id || null);
            setPersonaToDelete(null);
          }
        }}
        title="Remove Customer Profile"
        description={`Are you sure you want to remove "${personaToDelete?.name || ''}"? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />

      {/* On-demand AI generation modals (#5378 products / #5377 customer types) */}
      <GenerateItemModal
        open={productModalOpen}
        onOpenChange={setProductModalOpen}
        title={`Add ${sellsServices ? 'Service' : 'Product'}`}
        description={`Describe the ${sellsServices ? 'service' : 'product'} to add. Optional: leave blank to generate from your business profile.`}
        placeholder="e.g. Focus on our enterprise tier"
        submitLabel={`Generate ${sellsServices ? 'Service' : 'Product'}`}
        isGenerating={generateProductMutation.isPending}
        onGenerate={handleGenerateProduct}
      />
      <GenerateItemModal
        open={personaModalOpen}
        onOpenChange={setPersonaModalOpen}
        title="Add Customer Type"
        description="Describe the customer type to add. Optional: leave blank to generate from your business profile."
        placeholder="e.g. Enterprise procurement leads"
        submitLabel="Generate Customer Type"
        isGenerating={generatePersonaMutation.isPending}
        onGenerate={handleGeneratePersona}
      />

      {UnsavedChangesModal}
      </div>
    </div>
  );
}
